import { lazy, Suspense } from 'react';
import { PREVIEW_TYPES, getExt, getPreviewType } from '../utils/previewTypes';

// L'ANTEPRIMA PDF SI SCARICA SOLO SE SERVE (2026-09-12).
// pdf.js pesa piu' di tutto il resto del sito messo insieme. Con lazy() il
// suo pezzo di bundle parte al primo PDF aperto e mai prima: chi entra nel
// vault e guarda le sue foto non scarica una riga di pdf.js.
const PdfPreview = lazy(() => import('../components/PdfPreview'));
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { safeGetItem, safeSetItem } from '../utils/storage';
import { useFiles, svuotaMemoriaDidascalie } from '../hooks/useFiles';
import { useCurrency } from '../hooks/useCurrency';
import { formatBytes } from '../utils/crypto';
import ImageThumb, { puoAvereMiniatura, urlImmagine, svuotaMiniature, limiteMiniatura } from '../components/ImageThumb';
import CerimoniaChiave from '../components/CerimoniaChiave';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { prezzoTesto } from '../utils/prezzo';
import { unitaByte } from '../utils/unita';
import { dentroApp } from '../utils/dentroApp';
import { usePricing } from '../hooks/usePricing';
import InAppBrowserWarning from '../components/InAppBrowserWarning';
import BannerOfferta from '../components/BannerOfferta';
import SupportButton from '../components/SupportButton';
import ShareModal from '../components/ShareModal';
import SharedFilesModal from '../components/SharedFilesModal';
import VersionHistoryModal from '../components/VersionHistoryModal';
import { messaggioErrore } from '../utils/messaggioErrore';
import Dock from '../components/Dock';
import DockMenu from '../components/DockMenu';
import FamilyFlyout from '../components/FamilyFlyout';
import SearchFlyout from '../components/SearchFlyout';
import FolderIcon from '../components/FolderIcon';
import { IconProfile, IconFamily, IconSupport, IconPlans, IconSignOut, IconUpload, IconSearch } from '../components/icons';
import { Capacitor } from '@capacitor/core';
import { sessioneCombaciante, abbandonaSessione, sessioniAperte } from '../utils/caricamentoBlocchi';
import Trasferimenti from '../components/Trasferimenti';
import { creaMisuratore } from '../utils/velocita';
import BarraSpazio from '../components/BarraSpazio';
import { seguiAppiccicata } from '../utils/barreFisse';
import { avvisoInSospesoDalTelefono } from '../utils/nativeBackup';
import AvvisoAccount from '../components/AvvisoAccount';

// lifetime_1tb/lifetime_2tb use the vault's own accent-fill token value
// (2026-08-28 light palette) — this drives FILLS ONLY (the storage bar
// track, the sidebar quota bar, the upload progress bar, the dockmenu
// avatar gradient): no text renders directly on any of them, so the
// brighter, more vivid green is the right call there (AA text-contrast
// doesn't apply to a plain fill). The one place this same value used to
// ALSO drive actual text (the free-space number) now reads from
// PLAN_TEXT_COLORS instead — see planColorText below — because the
// brighter fill green only clears 3.38:1 on white, below AA for text.
// free's indigo is untouched either way, it was never tied to the green
// tokens.
// IL GRATUITO NON E' PIU' INDACO (2026-09-19). #6366f1 non appartiene alla
// tavolozza del sito: era l'unico blu di tutta l'interfaccia, e su "836,3 MB"
// sembrava un collegamento. Diventa un grigio scuro della stessa famiglia dei
// testi. Il verde non si usa qui: il verde e' delle azioni.
const PLAN_COLORS = { free:'#5A6661', lifetime_1tb:'#17A05C', lifetime_2tb:'#17A05C' };
const PLAN_TEXT_COLORS = { free:'#3A4741', lifetime_1tb:'#14804A', lifetime_2tb:'#14804A' };
const PLAN_LABELS = { free:'Free', lifetime_1tb:'1 TB Lifetime', lifetime_2tb:'2 TB Lifetime' };
// 2026-09-08: via l'infinito dai piani a vita. E' l'emoji U+267E, che i
// font emoji disegnano come un riquadro VIOLA — un colore che non esiste
// nella palette del prodotto, e che stonava accanto al nome del piano.
// Il 🆓 del piano gratuito resta: non era in discussione ed e' l'unico
// segno che distingue a colpo d'occhio chi non ha ancora comprato.
const PLAN_EMOJIS = { free:'🆓', lifetime_1tb:'', lifetime_2tb:'' };

const EXT_MAP = {
  pdf:['#DC2626','PDF'], doc:['#1D4ED8','DOC'], docx:['#1D4ED8','DOC'],
  txt:['#475569','TXT'], csv:['#047857','CSV'], xls:['#047857','XLS'], xlsx:['#047857','XLS'],
  jpg:['#B45309','JPG'], jpeg:['#B45309','JPG'], png:['#B45309','PNG'],
  gif:['#B45309','GIF'], webp:['#B45309','IMG'], svg:['#B45309','SVG'],
  mp4:['#6D28D9','MP4'], mov:['#6D28D9','MOV'], mkv:['#6D28D9','MKV'],
  mp3:['#BE185D','MP3'], wav:['#BE185D','WAV'], flac:['#BE185D','FLC'],
  zip:['#C2410C','ZIP'], tar:['#C2410C','TAR'], gz:['#C2410C','GZ'], rar:['#C2410C','RAR'],
  js:['#A16207','JS'], ts:['#1D4ED8','TS'], py:['#047857','PY'],
  jsx:['#0E7490','JSX'], json:['#C2410C','JSON'], html:['#DC2626','HTML'],
};
// Extensions rendered/edited with monospace "code" styling (vs. plain text)
const CODE_EXTS = ['js','ts','jsx','tsx','css','html','json','py','sh','bash','zsh',
  'yml','yaml','xml','ini','cfg','conf','sql','php','java','c','cpp','h','hpp','go','rb','rs','toml'];

// Middle-truncation for file names: CSS text-overflow:ellipsis alone always
// clips the END of the string, which for a file name means the extension —
// "MyIMG_2026.A…" instead of "MyIMG…926a3f.png". Splitting into a head (lets
// CSS ellipsis it from the right, in the flexible space) and a fixed tail
// (rendered in full, never clipped) keeps the extension visible regardless
// of how narrow the row gets. Short names aren't split at all — nothing to
// gain, and an unnecessary split could visually cut a short name in two for
// no reason.
function splitNameForTruncation(name, tailLen = 10) {
  if (!name || name.length <= tailLen + 6) return [name || '', ''];
  return [name.slice(0, name.length - tailLen), name.slice(-tailLen)];
}

function FileAvatar({ filename, size=40 }) {
  if (!filename || filename==='(encrypted)')
    return <div className="vz-avatar vz-avatar-enc" style={{width:size,height:size,fontSize:size*.42}}>🔒</div>;
  const ext=getExt(filename);
  const [color,label]=EXT_MAP[ext]||['#475569',ext.toUpperCase().slice(0,4)||'?'];
  return <div className="vz-avatar" style={{width:size,height:size,background:color+'18',border:`1.5px solid ${color}33`}}>
    <span style={{fontSize:9,fontWeight:800,color,fontFamily:'monospace',letterSpacing:'-.3px'}}>{label}</span>
  </div>;
}
function FolderAvatar({ size=40, name='' }) {
  return <FolderIcon name={name} size={size}/>;
}

// Controlled: parent (VaultPage) owns edit/draft state so it can save via
// replaceFile() — this component just loads the initial text (reporting it
// up via onLoaded) and renders either the read-only view or the textarea.
function TextPreview({ url, filename, editing, draft, onDraftChange, onLoaded, onLoadError }) {
  // 'plans' oltre a 'vault': la scheda del piano gratuito riusa le stringhe
  // della pagina dei piani invece di duplicarle (2026-09-10).
  const { t } = useTranslation(['vault', 'plans']);
  const [text, setText] = useState(''); const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(url).then(r=>r.text()).then(txt=>{ setText(txt); setLoading(false); onLoaded?.(txt); })
      .catch(()=>{ setText(t('text_unable_to_load')); setLoading(false); onLoadError?.(); });
  }, [url]); // eslint-disable-line
  const isCode = CODE_EXTS.includes(getExt(filename));
  if (loading) return <div className="pv-text-loading">{t('loading')}</div>;
  if (editing) {
    return <textarea className={`pv-text pv-text-edit ${isCode?'pv-code':''}`}
      value={draft} onChange={e=>onDraftChange(e.target.value)} spellCheck={false} autoFocus/>;
  }
  return <pre className={`pv-text ${isCode?'pv-code':''}`}>{text}</pre>;
}

// ── Context Menu ──────────────────────────────────────────
function ContextMenu({ x, y, items, onClose }) {
  const ref = useRef();
  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);
  // Adjust position if near screen edges
  const adjX = x + 200 > window.innerWidth ? x - 200 : x;
  const adjY = y + (items.length * 36) > window.innerHeight ? y - (items.length * 36) : y;
  return (
    <div ref={ref} className="ctx-menu" style={{ left: adjX, top: adjY }}>
      {items.map((item, i) =>
        item.sep ? <div key={i} className="ctx-sep"/> :
        <button key={i} className={`ctx-item ${item.danger?'ctx-danger':''} ${item.disabled?'ctx-disabled':''}`}
          onClick={() => { if (!item.disabled) { item.action(); onClose(); } }}>
          <span className="ctx-icon">{item.icon}</span>
          <span>{item.label}</span>
          {item.shortcut && <span className="ctx-shortcut">{item.shortcut}</span>}
        </button>
      )}
    </div>
  );
}

// ── Folder Tree (sidebar) ─────────────────────────────────
function FolderTree({ allFolders = [], currentFolder, onNavigate }) {
  const roots = allFolders.filter(f => !f.parentId);
  function renderNode(folder, depth=0) {
    const children = (allFolders||[]).filter(f => f.parentId === folder.id);
    const isActive = currentFolder === folder.id;
    return (
      <div key={folder.id}>
        <button
          className={`tree-node ${isActive?'active':''}`}
          style={{ paddingLeft: 12 + depth*14 }}
          onClick={() => onNavigate(folder)}
          /* Il suggerimento serve a colonna stretta, dove resta la sola
             icona della cartella: senza, quelle icone non si distinguono. */
          title={folder.name}
        >
          <FolderAvatar size={16} name={folder.name}/>
          <span className="tree-name">{folder.name}</span>
          {children.length > 0 && <span className="tree-count">{children.length}</span>}
        </button>
        {children.map(child => renderNode(child, depth+1))}
      </div>
    );
  }
  return (
    <div className="folder-tree">
      {roots.map(f => renderNode(f))}
    </div>
  );
}

export default function VaultPage({ onUpgrade, onAdmin, onProfile, onLogout, onTerms, onPrivacy, initialFolderId }) {
  const { t, i18n } = useTranslation('vault');
  const { user, isAdmin, encKey, sbloccatoDalDispositivo } = useAuth();

  // LO DICE, LA PRIMA VOLTA (2026-09-12). Un telefono che smette di chiedere
  // la password senza spiegare perche' sembra un difetto, non una comodita'.
  // Una volta sola per dispositivo: la seconda e' gia' un'abitudine.
  const [avvisoSblocco, setAvvisoSblocco] = useState(false);
  useEffect(() => {
    if (!sbloccatoDalDispositivo) return;
    try {
      if (localStorage.getItem('lc_sblocco_spiegato') === '1') return;
      localStorage.setItem('lc_sblocco_spiegato', '1');
    } catch { /* senza storage lo si dice ogni volta: meglio due volte che mai */ }
    setAvvisoSblocco(true);
  }, [sbloccatoDalDispositivo]);
  const {
    files, folders, allFolders, quota, loading, uploading, uploadProgress,
    currentFolder, folderPath,
    recentFiles, recentLoading, loadRecentFiles,
    preferiti, preferitiLoading, loadPreferiti, togglePreferito,
    loadFiles, openFolder, navigateTo, navigateToFolderId, searchAllFiles,
    createFolder, deleteFolder, renameFolder, moveFolder,
    initDefaultFolders = () => {},
    uploadFile, replaceFile, downloadFile, deleteFile, moveFile, salvaDidascalia,
    elencaSessioniAperte, rileggiStato
  } = useFiles();
  // Only one Dock flyout (Profile "Start menu" / Family / Search) can be open at a time —
  // they all render in the same bottom-center slot, so opening one closes the others.
  const [openFlyout, setOpenFlyout] = useState(null); // null | 'profile' | 'family' | 'search'
  const profileMenuOpen = openFlyout === 'profile';
  const familyMenuOpen  = openFlyout === 'family';
  const searchOpen      = openFlyout === 'search';
  function toggleFlyout(name) { setOpenFlyout(o => o === name ? null : name); }

  // Da dove e' stato aperto il menu del profilo: dalla dock in basso o dal
  // pulsante dell'account in alto a destra. Serve solo a decidere DOVE
  // compare — le voci sono le stesse in tutti e due i casi.
  const bottoneAccountRef = useRef(null);
  const [menuDaAccount, setMenuDaAccount] = useState(false);
  function closeFlyout() { setOpenFlyout(null); }

  // Mobile bottom nav: 'home' shows the regular folder view, 'recent' shows recently uploaded files
  const [mobileTab, setMobileTab] = useState('home');
  function goHomeTab() { setMobileTab('home'); navigateTo(null, -1); closeSidebarOnMobile(); }
  function goRecentTab() { setMobileTab('recent'); loadRecentFiles(); closeSidebarOnMobile(); }
  function goPreferitiTab() { setMobileTab('preferiti'); loadPreferiti(); closeSidebarOnMobile(); }

  // LA STELLINA (2026-09-18). Un clic cambia e basta: niente ricaricamento
  // della cartella, la riga si aggiorna da sola (vedi togglePreferito in
  // useFiles). Se il server rifiuta si torna com'era e si mostra l'errore —
  // mai una stellina piena che in realta' non e' stata salvata.
  const [preferitoBusy, setPreferitoBusy] = useState(null);
  async function cambiaPreferito(file) {
    if (preferitoBusy) return;
    setPreferitoBusy(file.id);
    try { await togglePreferito(file.id, !file.preferitoAt); }
    // Stesso modo in cui questa pagina riferisce gli altri errori brevi
    // (vedi handleRename/handleMove piu' sotto): non si inventa un canale nuovo.
    catch (e) { alert(messaggioErrore(e, t)); }
    finally { setPreferitoBusy(null); }
  }

  const [dragOver,       setDragOver]       = useState(false);
  const [uploadError,    setUploadError]    = useState('');
  // Avanzamento del trascinamento di una cartella: { fatti, totale, nome }.
  // null quando non c'e' nessuna cartella in corso.
  const [avanzamentoCartella, setAvanzamentoCartella] = useState(null);
  const [actionId,       setActionId]       = useState(null);
  // VISTA PREDEFINITA = GRIGLIA (2026-09-16), e la scelta si ricorda.
  // Chiave PER UTENTE, non per sessione: chi passa all'elenco lo ritrova la
  // volta dopo. Vive in localStorage, quindi per dispositivo — non viaggia fra
  // telefono e computer (per quello servirebbe una preferenza sul server).
  // `user` e' dichiarato sopra (useAuth): nessun uso prima della dichiarazione.
  const chiaveVista = `ltc-vista-vault:${user?.id || 'anon'}`;
  const [viewMode,       setViewModeStato] = useState(() => (safeGetItem(chiaveVista) === 'list' ? 'list' : 'grid'));
  const setViewMode = useCallback((v) => { setViewModeStato(v); safeSetItem(chiaveVista, v); }, [chiaveVista]);
  // Se cambia l'utente (stesso browser, altro account) si rilegge la SUA scelta.
  useEffect(() => { setViewModeStato(safeGetItem(chiaveVista) === 'list' ? 'list' : 'grid'); }, [chiaveVista]);
  const [sortBy,         setSortBy]         = useState('date');
  const [sortDir,        setSortDir]        = useState('desc');
  const [selected,       setSelected]       = useState(new Set());
  const [downloading,    setDownloading]    = useState(false);
  const [showNewFolder,  setShowNewFolder]  = useState(false);
  const [newFolderName,  setNewFolderName]  = useState('');
  const [folderError,    setFolderError]    = useState('');
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue,    setRenameValue]    = useState('');
  const [preview,        setPreview]        = useState(null);
  const [previewEditing, setPreviewEditing] = useState(false);
  const [previewDraft,   setPreviewDraft]   = useState('');
  const [previewLoaded,  setPreviewLoaded]  = useState('');
  const [previewLoadOk,  setPreviewLoadOk]  = useState(false); // false until text has loaded successfully — gates Edit so a failed load can't be "saved" as empty
  const [previewSaving,  setPreviewSaving]  = useState(false);
  const [ctxMenu,        setCtxMenu]        = useState(null); // {x,y,items}
  const [sidebarOpen,    setSidebarOpen]    = useState(() => window.innerWidth > 700);
  // LA COLONNA STRETTA (2026-09-19). Da desktop la colonna non sparisce piu':
  // si stringe a una striscia di sole icone, cosi' si vede che c'e' ancora e
  // che si riapre, e non si perde l'orientamento. La scelta resta su questo
  // browser: chi la chiude la ritrova chiusa alla visita dopo. Su telefono
  // questo stato non conta: li' comanda sidebarOpen, che apre e chiude il
  // pannello a scomparsa come sempre.
  const [colonnaStretta, setColonnaStretta] = useState(() => safeGetItem('lc_colonna_stretta') === '1');
  function cambiaColonna() {
    setColonnaStretta((c) => { const n = !c; safeSetItem('lc_colonna_stretta', n ? '1' : '0'); return n; });
  }
  function closeSidebarOnMobile() { if (window.innerWidth <= 700) setSidebarOpen(false); }
  const supportRef = useRef(null);

  // Recovery token modal
  const [newRecoveryToken, setNewRecoveryToken] = useState(() => sessionStorage.getItem('zv_pending_recovery')||'');
  const [newRecoveryUser,  setNewRecoveryUser]  = useState(() => sessionStorage.getItem('zv_pending_recovery_user')||'');
  const [recoveryDlDone,   setRecoveryDlDone]   = useState(false);
  const [recoveryCopied,   setRecoveryCopied]   = useState(false); // stays true once copied — also gates the skip-confirm below
  const [moveModal, setMoveModal] = useState(null);
  const [moveError, setMoveError] = useState('');
  const [shareTarget, setShareTarget] = useState(null); // {type,id,name,raw} — open ShareModal
  const [sharedModalOpen, setSharedModalOpen] = useState(false);
  const [versionsTarget, setVersionsTarget] = useState(null); // file — opens VersionHistoryModal
  const [replaceTarget, setReplaceTarget] = useState(null);   // file currently being replaced
  const replaceInputRef = useRef();

  function dismissRecovery() {
    sessionStorage.removeItem('zv_pending_recovery');
    sessionStorage.removeItem('zv_pending_recovery_user');
    setNewRecoveryToken(''); setNewRecoveryUser(''); setRecoveryDlDone(false); setRecoveryCopied(false);
  }
  // Skipping without ever downloading or copying the key means losing the
  // only way to recover the account — confirm explicitly in that case only;
  // once either action happened, Skip/Continue proceeds straight away.
  function handleSkipRecovery() {
    if (!recoveryDlDone && !recoveryCopied && !window.confirm(t('recovery_skip_confirm'))) return;
    dismissRecovery();
  }
  function copyRecoveryToken() {
    navigator.clipboard.writeText(newRecoveryToken).then(() => setRecoveryCopied(true)).catch(() => {});
  }
  function downloadRecoveryFile() {
    // Stesso file che scarica AuthPage alla registrazione: stesse chiavi (ns auth), data nella lingua dell'utente (2026-09-16; prima inglese fisso).
    const content = [t('auth:recovery_token_file_header'),'================================','',t('auth:recovery_token_file_account_label')+' '+newRecoveryUser,t('auth:recovery_token_file_date_label')+' '+formatDateTime(new Date(), i18n.language),'',t('auth:recovery_token_file_key_label'),newRecoveryToken,'','================================',t('auth:recovery_token_file_keep_safe'),'================================'].join('\n');
    const blob=new Blob([content],{type:'text/plain'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a'); a.href=url; a.download=`lifetimecloud-recovery-${newRecoveryUser}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(()=>URL.revokeObjectURL(url),1000);
    setRecoveryDlDone(true);
  }

  const fileInputRef   = useRef();
  const folderInputRef = useRef();
  const mainRef        = useRef();
  const headerRef      = useRef();

  // Whether this device can actually drive a directory picker: a plain
  // width check misreads iPad Safari in landscape (~1024px, touch-only) as
  // desktop. hover:hover + pointer:fine is true only for mouse/trackpad
  // input — every touch device (phone or tablet) reports pointer:coarse
  // regardless of screen width. Read via a listener, not just on mount, so
  // it flips live if the window is resized across the breakpoint.
  const [canPickDirectory, setCanPickDirectory] = useState(() =>
    window.matchMedia('(min-width: 700px) and (hover: hover) and (pointer: fine)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 700px) and (hover: hover) and (pointer: fine)');
    const onChange = () => setCanPickDirectory(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    // A real /vault/:folderId link (deep link, or the page restoring one
    // after a reload while nested — see App.js's initialFolderId capture)
    // opens straight into that folder instead of always landing at root.
    // pushHistory:false — the URL already has this folder, nothing to push.
    if (initialFolderId) navigateToFolderId(initialFolderId, { pushHistory: false });
    else loadFiles(null);
    if (typeof initDefaultFolders === 'function') initDefaultFolders();
  }, []); // eslint-disable-line

  // Mobile folder grid's "show all" shouldn't carry over into a different
  // folder that might only have 2 subfolders.

  // On mobile, the storage/plan bar sits sticky right below the header
  // (see .dock-storage-wrap's mobile rule in App.css) instead of overlapping
  // the file list at the bottom — but the header's real height varies (it
  // wraps to extra rows depending on content/breakpoint), so a hardcoded
  // `top` would drift out of sync and either leave a gap or overlap again.
  // Stesso schema con ResizeObserver usato altrove per misurare dal vivo
  // l'altezza di un elemento e passarla al CSS (vedi --card-offerta-h).
  useEffect(() => {
    if (!headerRef.current) return;
    const el = headerRef.current;
    const root = document.documentElement;
    const update = () => root.style.setProperty('--vz-header-h', `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { ro.disconnect(); root.style.removeProperty('--vz-header-h'); };
  }, []);

  // LA PILA APPICCICATA (2026-09-13). Intestazione, pastiglia dello spazio,
  // riga dei filtri e riga dei titoli: su telefono restano tutte visibili
  // mentre l'elenco scorre, quindi ognuna deve fermarsi sotto la precedente.
  // Le altezze non si scrivono: si misurano dal vivo e le somma il registro
  // (utils/barreFisse.js), lo stesso che impila le barre fisse in cima — cosi'
  // quando compare l'avviso dello spazio scende tutta la pila insieme.
  //
  // Su desktop ci entra la sola intestazione: li' la pastiglia e' ancorata in
  // basso, i filtri non esistono e la tabella scorre dentro .vz-main.
  useEffect(() => {
    const stretto = window.matchMedia('(max-width: 700px)');
    let spegni = [];
    let ultimi = null;
    const monta = () => {
      const pezzi = stretto.matches
        ? [['testa', headerRef.current],
           ['pastiglia', document.querySelector('.dock-storage-wrap')],
           ['ordina', document.querySelector('.vz-ordina-mobile')],
           ['tabella', document.querySelector('.vz-table-head')]]
        : [['testa', headerRef.current]];
      // Se sono gli stessi nodi di prima non si tocca niente: l'elenco cambia
      // di continuo mentre si naviga, e smontare e rimontare quattro
      // osservatori a ogni riga sarebbe lavoro per niente.
      const nodi = pezzi.map(([, el]) => el || null);
      if (ultimi && nodi.length === ultimi.length && nodi.every((n, i) => n === ultimi[i])) return;
      ultimi = nodi;
      spegni.forEach((f) => f());
      spegni = pezzi.map(([id, el]) => seguiAppiccicata(id, el));
    };
    monta();
    stretto.addEventListener('change', monta);
    // Le righe dei filtri e dei titoli compaiono e spariscono col contenuto
    // (cartella vuota, vista a griglia, ricerca senza esiti): si rimisura
    // quando l'albero sotto la colonna cambia, invece di fidarsi del primo giro.
    const mo = new MutationObserver(monta);
    const wrap = document.querySelector('.vz-main-wrap');
    if (wrap) mo.observe(wrap, { childList: true, subtree: true });
    return () => {
      stretto.removeEventListener('change', monta);
      mo.disconnect();
      spegni.forEach((f) => f());
    };
  }, []);

  // TUTTO il gruppo fisso in basso — striscia dell'offerta, pillola
  // "Free · 1 GB", barra delle icone e il suo menu — sta sull'asse della
  // COLONNA DEI CONTENUTI, non della finestra: prima pillola e dock erano
  // centrate sulla finestra intera e, con la barra laterale a sinistra,
  // il loro centro non coincideva con quello della tabella. Niente
  // costanti in CSS: si misura la colonna vera (.vz-main-wrap) e si
  // scrivono due variabili che il CSS usa come left/right. Stesso schema
  // del --vz-header-h qui sopra. Su mobile il CSS le ignora (media query:
  // niente sidebar, tutto com'era).
  useEffect(() => {
    const wrap = document.querySelector('.vz-main-wrap');
    if (!wrap) return;
    const root = document.documentElement;
    const update = () => {
      const r = wrap.getBoundingClientRect();
      root.style.setProperty('--colonna-sx', `${Math.round(r.left)}px`);
      root.style.setProperty('--colonna-dx', `${Math.round(window.innerWidth - r.right)}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    ro.observe(document.documentElement);   // il wrap può restare uguale mentre la finestra cambia
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect(); window.removeEventListener('resize', update);
      root.style.removeProperty('--colonna-sx'); root.style.removeProperty('--colonna-dx');
    };
  }, []);

  const planId    = user?.plan?.id || user?.plan || 'free';
  const planColor = PLAN_COLORS[planId] || '#6366f1';
  const planColorText = PLAN_TEXT_COLORS[planId] || '#6366f1';
  const planLabel = t(`plan_${planId}`, PLAN_LABELS[planId] || 'Free');
  const planEmoji = PLAN_EMOJIS[planId] ?? '🆓';

  // Same per-currency price source as PlansPage.jsx, so the sidebar CTA
  // always matches what checkout actually charges (falls back to the plain
  // 2TB price once this user's own welcome window has closed — see
  // hooks/usePricing.js's userPromo2tb, server-computed from
  // users.promo2tbExpiresAt, 2026-08-30 rework). VaultPage is always a
  // logged-in context, so the real per-user number applies unconditionally
  // here — never the anonymous always-on tb2LaunchPromo.
  const { currency } = useCurrency();
  const { priceOf, userPromo2tb, posti} = usePricing(currency);
  // 2026-09-01 — the two CTAs in this page are now mutually exclusive, and
  // each one states the price its OWN checkout path will actually charge.
  //
  // Real bug this fixes: the header/mobile pill showed the discounted
  // number (promo2tb.effective, €79) while starting checkout with campaign
  // 'vault_upgrade' — which is NOT the welcome-banner path, so the server
  // correctly charged €99. That is the one direction a price mismatch must
  // never point: the customer being charged MORE than the button promised.
  //
  // The pill's price therefore comes from the plain 2TB price, full stop —
  // the same number routes/payments.js will build a 'vault_upgrade' session
  // from. The discounted number lives only on the welcome banner, whose
  // 'promo_banner' campaign is the one the server actually honours.
  const promo2tb = userPromo2tb?.promo || null;
  const upgradePrice = priceOf('lifetime_2tb', currency)?.amount;
  const upgradeSymbol = priceOf('lifetime_2tb', currency)?.symbol;
  // I due riquadri 1 TB / 2 TB del vault (2026-09-07) non esistono piu'
  // (2026-09-16): l'offerta nel vault e' una sola, la striscia in fondo, che
  // ha il suo checkout. Il 1 TB si compra da /plans.
  // Neither half renders without the other — a price with no symbol (or
  // vice versa) is exactly the "undefined79" bug found live 2026-08-30.
  const upgradePriceReady = upgradePrice != null && upgradeSymbol != null;
  // showUpgradePill (free + prezzo + !welcomeWindowOpen) non esiste piu'
  // (2026-09-16 sera): era il guardiano della vecchia fascia in cima, e
  // agganciato per errore alla striscia in fondo la spegneva nelle 48 ore
  // dopo la registrazione. La striscia usa showUpgradeStrip, qui sotto.
  // La striscia in fondo NON ha il guardiano !welcomeWindowOpen: serviva a
  // non contraddire la scheda di benvenuto, eliminata il 2026-09-04 — da
  // allora un free nella finestra dei 2 giorni non vedeva NESSUN invito.
  // Nessuna contraddizione possibile oggi: il prezzo mostrato e' il listino
  // vero (priceOf) e il checkout parte dallo stesso flusso condiviso.
  // Ogni free la vede; chi ha acquistato mai, in nessuna misura.
  const showUpgradeStrip = planId === 'free' && upgradePriceReady;

  // I due checkout propri del vault (vault_upgrade / vault_upgrade_1tb) sono
  // stati tolti il 2026-09-16 insieme ai riquadri: l'unico acquisto dal vault
  // passa dalla striscia (BannerOfferta, campagna promo_bar). La voce "Piani"
  // nella dock e nel menu continua a portare a /plans.
  const quotaPct  = quota ? Math.min(100,(quota.used/quota.total)*100) : 0;
  const quotaWarn = quotaPct > 85;
  const freeSpace = quota ? (quota.free ?? quota.total - quota.used) : 0;

  // L'OMBRA DELLA BARRA IN ALTO (2026-09-19). Compare solo quando c'e'
  // qualcosa di scorso sotto: a lista ferma la barra e' piatta, e il filo di
  // bordo basta a separarla dall'area dei file.
  const [listaScorsa, setListaScorsa] = useState(false);
  useEffect(() => {
    const el = document.querySelector('.vz-main');
    const bersaglio = el || window;
    const leggi = () => setListaScorsa((el ? el.scrollTop : window.scrollY) > 4);
    leggi();
    bersaglio.addEventListener('scroll', leggi, { passive: true });
    return () => bersaglio.removeEventListener('scroll', leggi);
  }, [mobileTab, currentFolder]);

  const isRecentView = mobileTab === 'recent';
  // I PREFERITI SONO UNA VISTA, non una cartella (2026-09-18). Stessa forma
  // di «Recenti»: un elenco di file che vengono da cartelle diverse, quindi
  // niente cartelle dentro, niente caricamento qui dentro (si carica nella
  // cartella vera) e niente navigazione a ritroso. `vistaSpeciale` e' il nome
  // delle due insieme, cosi' i controlli che valgono solo dentro una cartella
  // non devono elencarle una per una ogni volta che se ne aggiunge una.
  const isPreferitiView = mobileTab === 'preferiti';
  const vistaSpeciale = isRecentView || isPreferitiView;

  // L'ALTEZZA DEL FONDO FISSO (2026-09-16). Il contenitore .vz-fondo-fisso
  // impila "Carica file" e la striscia sopra la dock; qui si misura la sua
  // altezza vera e la si pubblica (--vz-fondo-h), cosi' la lista dei file le
  // lascia sotto lo spazio giusto e l'ultimo file non finisce coperto.
  // Misurata dal vivo (cambia col wrap del testo, con la barra che c'e' o no).
  useEffect(() => {
    const radice = document.documentElement;
    // QUANTO COPRE LA PILA, MISURATO (2026-09-19). Prima si sommavano a mano
    // l'altezza del contenitore e quella della dock, piu' un margine fisso: il
    // conto era sempre un po' sbagliato, e sbagliato in un modo che non si
    // poteva correggere senza rifarlo a mano a ogni cambio. Qui si guarda
    // invece il punto piu' ALTO raggiunto dagli strati che galleggiano in
    // fondo, e si riserva esattamente la distanza da li' al bordo dello
    // schermo. Cosi' lo spazio basta e basta: nessun file coperto, nessun
    // vuoto sotto l'ultimo.
    const STRATI = ['.vz-fondo-fisso', '.dock-wrap', '.dock-storage-wrap', '.vz-mobile-uploadbar'];
    const misura = () => {
      const el = document.querySelector('.vz-fondo-fisso');
      const h = el ? Math.ceil(el.getBoundingClientRect().height) : 0;
      if (h > 0) radice.style.setProperty('--vz-fondo-h', h + 'px');
      else radice.style.removeProperty('--vz-fondo-h');

      let piuAlto = window.innerHeight;
      for (const sel of STRATI) {
        const e = document.querySelector(sel);
        if (!e) continue;
        const st = getComputedStyle(e);
        if (st.position !== 'fixed' || st.display === 'none' || st.visibility === 'hidden') continue;
        const r = e.getBoundingClientRect();
        // Solo quelli davvero appoggiati in fondo: uno strato che sta in alto
        // (la pastiglia su telefono, che e' sticky sotto l'intestazione) non
        // occupa spazio in fondo e non va contato.
        if (r.height <= 0 || r.bottom < window.innerHeight * 0.6) continue;
        piuAlto = Math.min(piuAlto, r.top);
      }
      const occluso = Math.max(0, Math.ceil(window.innerHeight - piuAlto));
      radice.style.setProperty('--vz-occluso', occluso + 'px');
    };
    misura();
    const ro = new ResizeObserver(misura);
    for (const sel of STRATI) { const e = document.querySelector(sel); if (e) ro.observe(e); }
    window.addEventListener('resize', misura);
    window.addEventListener('orientationchange', misura);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', misura);
      window.removeEventListener('orientationchange', misura);
      radice.style.removeProperty('--vz-fondo-h');
      radice.style.removeProperty('--vz-occluso');
    };
  }, [isRecentView, isPreferitiView, planId, showUpgradeStrip]);
  const viewFolders  = vistaSpeciale ? [] : folders;
  // FILE NASCOSTI (2026-09-06). Nel vault comparivano .DS_Store e
  // ._.DS_Store: file di servizio che macOS scrive da solo in ogni cartella
  // che apre, e che nessun gestore di file mostra — arrivano da WebDAV
  // (Finder, Cyberduck), dove il client copia la cartella cosi' com'e'.
  // Si nascondono tutti i nomi che iniziano con un punto, che e' la
  // convenzione universale, e si lascia l'interruttore a chi li vuole
  // vedere. La scelta resta memorizzata: chi la accende non deve rifarlo
  // a ogni visita.
  // Quale file ha aperta la bollicina "perche' non e' cifrato" (uno solo).
  const [spiegaCifratura, setSpiegaCifratura] = useState(null);
  const [mostraNascosti, setMostraNascosti] = useState(() => safeGetItem('lc_mostra_nascosti') === '1');
  function nascosto(nome) { return typeof nome === 'string' && nome.startsWith('.'); }
  const fileGrezzi   = isRecentView ? recentFiles : isPreferitiView ? preferiti : files;
  const nascostiN    = fileGrezzi.filter(f => nascosto(f.filename)).length;
  const viewFiles    = mostraNascosti ? fileGrezzi : fileGrezzi.filter(f => !nascosto(f.filename));
  const viewLoading  = isRecentView ? recentLoading : isPreferitiView ? preferitiLoading : loading;
  // VAULT COMPLETAMENTE VUOTO (2026-09-05): nessun file e nessuna cartella,
  // alla radice, fuori dalla vista "Recenti". E' la schermata dove si ferma
  // la maggior parte dei nuovi iscritti: si mostra UN SOLO blocco centrato
  // con l'istruzione, e si nasconde tutto il resto — lista, offerta, colonne.
  const vaultDelTuttoVuoto = !vistaSpeciale && !currentFolder
    && files.length === 0 && allFolders.length === 0 && !loading;


  // Sorting
  const sortedFiles = [...viewFiles].sort((a,b) => {
    let va,vb;
    if (sortBy==='name') { va=a.filename||''; vb=b.filename||''; return sortDir==='asc'?va.localeCompare(vb):vb.localeCompare(va); }
    if (sortBy==='size') { va=a.size||0; vb=b.size||0; }
    else { va=new Date(a.uploadedAt); vb=new Date(b.uploadedAt); }
    return sortDir==='asc'?va-vb:vb-va;
  });
  function toggleSort(col) {
    if (sortBy===col) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortBy(col); setSortDir('desc'); }
  }

  // Selection
  function toggleSelect(id,e) {
    e.stopPropagation();
    setSelected(prev => { const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  }
  function selectAll() { setSelected(s => s.size===viewFiles.length ? new Set() : new Set(viewFiles.map(f=>f.id))); }
  const hasSelection = selected.size > 0;
  const allSelected  = viewFiles.length > 0 && selected.size === viewFiles.length;

  // Upload
  // FILE_LIMIT gets its own copy instead of the backend's raw (English,
  // "here's what you can't do") string: says what upgrading gets you,
  // in the viewer's language, using the same live promo price as the
  // header CTA above (never a hand-typed "€79" that could drift from
  // what checkout actually charges). QUOTA_EXCEEDED keeps the older,
  // generic upgrade_plan_suffix treatment — not asked to change tonight.
  function formatUploadError(e) {
    // Il file non si e' potuto leggere dal disco (NotReadableError /
    // NotFoundError): cartella sincronizzata non ancora scaricata, oppure file
    // spostato dopo averlo scelto. Non e' un errore del server e non arriva da
    // li': lo alza il browser durante la lettura, e utils/crypto.js lo
    // riconosce e lo marca con questo codice. Il testo tecnico e' gia' andato
    // in console — a schermo va solo la frase che dice cosa fare.
    if (e.code === 'FILE_NON_LEGGIBILE') return t('upload_non_leggibile');
    if (e.code === 'FILE_LIMIT') {
      return promo2tb
        ? t('file_limit_reached_promo', { maxFiles: e.maxFiles, price: prezzoTesto(upgradeSymbol, upgradePrice, i18n.language) })
        : t('file_limit_reached', { maxFiles: e.maxFiles });
    }
    // Backend's e.message here is a hand-typed English string (files.js) —
    // never shown as-is, since the UI must localize to the viewer's language.
    if (e.code === 'FILE_TOO_LARGE') {
      // Stessa frase del controllo nel browser, cosi' l'utente legge la stessa
      // cosa da qualunque parte arrivi il rifiuto. Il server non manda la
      // dimensione del file (non l'ha letta, e' il senso della correzione):
      // quando manca si dice solo il limite.
      const limite = pesoLeggibile(e.maxFileSize || 50 * 1024 * 1024);
      return e.fileSize
        ? t('upload_troppo_grande', { dimensione: pesoLeggibile(e.fileSize), limite })
        : t('upload_troppo_grande_solo_limite', { limite });
    }
    // SPAZIO ESAURITO, detto in italiano e con un numero dentro.
    //
    // Prima finiva nell'ultima riga qui sotto e diventava "Storage quota
    // exceeded. — Fai l'upgrade del piano.": il testo inglese del server piu'
    // un suffisso. Nelle quattro settimane prima del 13/09 l'hanno letto
    // dodici persone, e nessuna ha saputo quanto fosse grande il suo piano.
    //
    // La dimensione arriva dal rifiuto stesso (quotaBytes). Se il server e'
    // vecchio e non la manda, si usa quella che il vault ha gia' in mano; se
    // non c'e' nemmeno quella, si dice la frase senza numero invece di
    // inventarne uno.
    // Il browser non sa consegnare un file grosso (niente finestra di
    // salvataggio, niente spazio di lavoro): prima era un alert, adesso e' il
    // motivo scritto nella riga.
    if (e.code === 'SCARICO_NON_SUPPORTATO') return t('scarico_non_supportato');
    if (e.code === 'QUOTA_EXCEEDED') {
      const tetto = e.quotaBytes || quota?.total;
      const frase = tetto
        ? t('spazio_esaurito', { piano: pesoLeggibile(tetto) })
        : t('spazio_esaurito_senza_misura');
      // Dentro l'app il piano non si cambia: il pagamento vive sul sito, e un
      // collegamento qui porterebbe fuori dalla WebView. Si dice dove andare,
      // a parole, senza link.
      return frase + (Capacitor.isNativePlatform() ? ' ' + t('spazio_esaurito_app') : '');
    }
    return e.message + (e.code === 'QUOTA_EXCEEDED' ? t('upgrade_plan_suffix') : '');
  }
  // Checked once for the whole batch before any upload starts — maxFiles is
  // a total across the account, so without this a batch that would blow
  // past it still uploads however many files fit before the server finally
  // rejects one partway through (isBatchFatalUploadError only stops the
  // *rest* of the batch, it doesn't undo what already succeeded).
  // formatBytes da' sempre "2.1 GB": il punto e' la virgola decimale inglese.
  // In italiano si scrive 2,1 — e qui il numero finisce dentro una frase
  // letta da una persona, non in una tabella tecnica.
  function pesoLeggibile(byte) {
    const s = formatBytes(byte);
    return (i18n.language === 'it' || i18n.language === 'es' || i18n.language === 'fr' || i18n.language === 'de') ? s.replace('.', ',') : s;
  }

  // IL LIMITE SI CONTROLLA PRIMA DI LEGGERE IL FILE (2026-09-13).
  //
  // Com'era: nessun controllo nel browser. Si leggeva il file in memoria, lo si
  // cifrava, lo si spediva tutto, e SOLO ALLORA il server rispondeva 403. Su un
  // file da 1,6 GB e una linea da 1,8 MB/s sono quindici minuti di attesa per
  // sentirsi dire di no — e nel frattempo il browser aveva tenuto in memoria
  // tre copie del file.
  //
  // file.size e' un dato che il sistema operativo ha gia': leggerlo non apre il
  // file e non costa niente. Il controllo sta qui, all'inizio, prima persino
  // della cerimonia della chiave.
  function primoFileTroppoGrande(arr) {
    const limite = quota?.maxFileSize;
    if (!limite || limite < 0) return null;   // limite sconosciuto: non si blocca niente
    return arr.find(f => (f?.size || 0) > limite) || null;
  }

  // Vero se c'era un file troppo grande: chi chiama si ferma. Il messaggio
  // dice il peso del file e il limite, in chiaro — non un codice.
  function fermaSeTroppoGrande(arr) {
    const grosso = primoFileTroppoGrande(arr);
    if (!grosso) return false;
    setUploadError(t('upload_troppo_grande', {
      dimensione: pesoLeggibile(grosso.size),
      limite: pesoLeggibile(quota.maxFileSize),
    }));
    return true;
  }

  function checkBatchFileLimit(addCount) {
    if (!quota || quota.maxFiles === -1) return true;
    if (quota.fileCount + addCount > quota.maxFiles) {
      setUploadError(formatUploadError({ code: 'FILE_LIMIT', maxFiles: quota.maxFiles }));
      return false;
    }
    return true;
  }
  // Errors that make every *remaining* file in this batch fail too, not just
  // the one that triggered them: the plan's file-count/storage ceiling is
  // hit, or the session itself is no longer valid. Continuing the loop past
  // one of these just re-sends the same doomed request per remaining file —
  // one real case sent 588 rejected uploads in 3.5 minutes this way.
  // FILE_TOO_LARGE is deliberately NOT included: it's a property of that one
  // file's size vs. the plan's per-file cap, not of the batch — a smaller
  // file later in the same queue can still legitimately succeed.
  function isBatchFatalUploadError(e) {
    return e.code === 'FILE_LIMIT' || e.code === 'QUOTA_EXCEEDED' || e.status === 401;
  }
  // ── La cerimonia della chiave precede il PRIMO caricamento ────
  //
  // Finche' l'utente non ha salvato la sua chiave di recupero, nessun file
  // parte. Non e' un promemoria che si chiude: i file scelti restano in
  // attesa, e il caricamento riprende da solo appena la cerimonia e'
  // completata.
  //
  // LA DECISIONE LA PRENDE IL SERVER, A OGNI TENTATIVO. La prima stesura
  // decideva da uno stato React derivato da zv_user, e un ricaricamento a
  // meta' cerimonia la aggirava: dopo il reload nessuno chiedeva piu' se la
  // conferma fosse avvenuta davvero. Ora ogni tentativo di caricamento
  // interroga /auth/me e guarda recoveryKeyConfirmed: se il server dice che
  // la cerimonia non e' stata completata, si apre lo schermo e il file non
  // parte. Nessuna variabile di pagina e nessun sessionStorage partecipano
  // alla decisione — sessionStorage RICORDA il token (cosi' dopo un reload
  // si rivede la stessa chiave, non una nuova), ma non decide mai.
  //
  // Il costo e' una chiamata leggera in piu' all'inizio di ogni selezione di
  // file; il guadagno e' che il blocco non ha stato da corrompere. La regola
  // non distingue gratuiti da paganti: chi ha pagato e non ha ancora
  // confermato la vede comunque — e' chi ha piu' da perdere. I 355 utenti
  // esistenti sono gia' a confermato dal server e non vedranno niente.
  const [fileInAttesa, setFileInAttesa] = useState(null);

  async function cerimoniaConfermataDalServer() {
    const { apiMe } = await import('../utils/api');
    const me = await apiMe();
    return (me.user?.recoveryKeyConfirmed ?? me.recoveryKeyConfirmed) === true;
  }

  async function handleFiles(fl) {
    const arr = Array.from(fl);
    if (!arr.length) return;
    // Prima di ogni altra cosa, anche prima di chiedere al server della
    // cerimonia: se il file e' troppo grande non si legge, non si cifra, non
    // si invia. Si risponde subito.
    setUploadError('');
    // Il file troppo grande non ferma piu' tutto il gruppo: entra in coda gia'
    // fallito con la sua ragione, e gli altri partono lo stesso.
    let confermata;
    try {
      confermata = await cerimoniaConfermataDalServer();
    } catch {
      // Il server non risponde: nemmeno l'upload potrebbe partire. Meglio un
      // errore esplicito che un blocco deciso alla cieca — aprire la
      // cerimonia a un utente GIA' confermato gli rigenererebbe la chiave,
      // invalidando quella che ha salvato.
      setUploadError(t('chiave_verifica_offline'));
      return;
    }
    if (!confermata) { setFileInAttesa(arr); return; }
    return caricaDavvero(fl);
  }

  // ── LA CODA DEI CARICAMENTI (2026-09-13) ────────────────────────────────
  //
  // Scegliere dei file non carica: ACCODA. Un motore consuma la coda un file
  // alla volta — i quattro blocchi in parallelo dentro un file restano come
  // sono, quello che cambia e' che due FILE non partono piu' insieme.
  //
  // Ogni riga ha il suo stato e il suo esito. Prima c'era un indicatore solo e
  // un banner solo: con dieci file, l'errore del decimo cancellava quello del
  // terzo e non si sapeva piu' cosa fosse andato storto.
  //
  // Stati: attesa -> in-corso -> fatto | fallito | annullato
  //        chiede  = combacia con una sessione aperta, aspetta la tua scelta
  // IL MURO DELL'ACCOUNT. Spazio finito e limite di file non sono righe
  // fallite: fermano la coda e aprono un riquadro. Qui si tiene il motivo,
  // coi numeri del server dentro.
  const [muro, setMuro] = useState(null);

  // LA DIDASCALIA (2026-09-18). Testo dell'utente, cifrato nel suo browser
  // come lo erano i nomi nel ramo vecchio: il server tiene due stringhe e non
  // sa cosa dicono. Vuota = non esiste, e il file si comporta come prima.
  const [didascaliaTarget, setDidascaliaTarget] = useState(null);
  const [didascaliaTesto, setDidascaliaTesto]   = useState('');
  const [didascaliaErrore, setDidascaliaErrore] = useState('');
  const [didascaliaBusy, setDidascaliaBusy]     = useState(false);

  const [coda, setCoda] = useState([]);
  const codaRef = useRef([]);
  // I gruppi delle cartelle: una riga in cima ai figli, con nome e avanzamento
  // dell'insieme. Prima la cartella non compariva da nessuna parte.
  const [gruppi, setGruppi] = useState([]);
  const gruppiRef = useRef([]);
  // I misuratori stanno FUORI dallo stato: cambiano dieci volte al secondo e
  // rifare il disegno a ogni campione costerebbe piu' del trasferimento.
  const misuratori = useRef(new Map());
  const [totale, setTotale] = useState({ byteFatti: 0, byteTotali: 0, velocita: null, restano: null });
  const misuratoreTotale = useRef(creaMisuratore());
  const motoreAcceso = useRef(false);
  const annullatori = useRef(new Map());   // id riga -> AbortController
  const contatore = useRef(0);

  const scriviCoda = useCallback((f) => {
    codaRef.current = typeof f === 'function' ? f(codaRef.current) : f;
    setCoda(codaRef.current);
  }, []);
  const scriviGruppi = useCallback((f) => {
    gruppiRef.current = typeof f === 'function' ? f(gruppiRef.current) : f;
    setGruppi(gruppiRef.current);
  }, []);

  // Un colpo al secondo ricalcola velocita', tempo rimanente e totale. Non a
  // ogni campione: i campioni arrivano a raffica e servono solo al calcolo.
  useEffect(() => {
    const battito = setInterval(() => {
      let fattiTot = 0, tuttoTot = 0, cambiato = false;
      const aggiornate = codaRef.current.map((r) => {
        if (r.stato === 'in-corso' || r.stato === 'attesa' || r.stato === 'in-pausa') {
          fattiTot += r.byteFatti || 0;
          tuttoTot += r.byteTotali || 0;
        }
        if (r.stato !== 'in-corso') return r;
        const m = misuratori.current.get(r.id);
        if (!m) return r;
        const v = m.velocita();
        const q = m.restano(r.byteTotali);
        if (v === r.velocita && q === r.restano) return r;
        cambiato = true;
        return { ...r, velocita: v, restano: q };
      });
      if (cambiato) scriviCoda(aggiornate);

      // Il totale si misura come una riga sola: byte fatti di tutto quello che
      // e' ancora in ballo, sulla stessa finestra di cinque secondi.
      if (tuttoTot > 0) {
        misuratoreTotale.current.segna(fattiTot);
        setTotale({
          byteFatti: fattiTot, byteTotali: tuttoTot,
          velocita: misuratoreTotale.current.velocita(),
          restano: misuratoreTotale.current.restano(tuttoTot),
        });
      } else {
        misuratoreTotale.current.azzera();
      }

      // E l'avanzamento dei gruppi, che e' la somma dei loro figli.
      if (gruppiRef.current.length) {
        scriviGruppi((gg) => gg.map((g) => {
          const suoi = codaRef.current.filter((r) => r.gruppoId === g.id);
          return {
            ...g,
            byteFatti: suoi.reduce((t, r) => t + (r.byteFatti || 0), 0),
            byteTotali: suoi.reduce((t, r) => t + (r.byteTotali || 0), 0),
          };
        }));
      }
    }, 1000);
    return () => clearInterval(battito);
  }, [scriviCoda, scriviGruppi]);
  const aggiornaRiga = useCallback((id, dati) => {
    scriviCoda((c) => c.map((r) => (r.id === id ? { ...r, ...dati } : r)));
  }, [scriviCoda]);

  // Salvare con il campo vuoto significa togliere la didascalia: una cosa
  // sola da spiegare, e nessun bottone «cancella» da confondere con «annulla».
  async function salvaLaDidascalia() {
    if (!didascaliaTarget) return;
    setDidascaliaBusy(true); setDidascaliaErrore('');
    try {
      await salvaDidascalia(didascaliaTarget.id, didascaliaTesto);
      setDidascaliaTarget(null);
    } catch (e) {
      setDidascaliaErrore(messaggioErrore(e, t));
    } finally { setDidascaliaBusy(false); }
  }

  // ── AVVISO ALLA CHIUSURA ───────────────────────────────────────────────
  // Chiudere la scheda con la coda al lavoro butta via il caricamento in
  // corso: i blocchi gia' saliti restano sul server e si possono riprendere,
  // ma l'utente non lo sa e se ne va convinto di aver caricato tutto.
  useEffect(() => {
    const restaDaFare = coda.some((r) => r.stato === 'in-corso' || r.stato === 'attesa' || r.stato === 'chiede');
    if (!restaDaFare) return;
    const avvisa = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', avvisa);
    return () => window.removeEventListener('beforeunload', avvisa);
  }, [coda]);

  // ── ARRIVARE AL VAULT GIA' CONTRO IL MURO (2026-09-13) ─────────────────
  //
  // La notifica del telefono dice che il backup si e' fermato, la si tocca,
  // si apre l'app... e si e' esattamente dove si era prima: nessun banner,
  // nessuna spiegazione, nessuna idea di cosa fare. Segnalato dal vivo.
  //
  // Adesso, se l'account e' gia' contro un muro quando il vault si apre, il
  // riquadro si mostra da solo — una volta per visita, non a ogni ricarica
  // dell'elenco.
  // Schermo stretto: la pastiglia dello spazio cambia forma, non si accorcia
  // coi puntini. 480 px e' la soglia: sotto, la forma lunga non ci sta in una
  // riga nemmeno togliendo il nome del piano.
  const [strettissimo, setStrettissimo] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 480px)');
    const ascolta = (e) => setStrettissimo(e.matches);
    mq.addEventListener('change', ascolta);
    return () => mq.removeEventListener('change', ascolta);
  }, []);

  // Quale dei due muri e' stato raggiunto: serve a colorare di rosso il numero
  // giusto invece di tingere tutta la pastiglia.
  const fileAlMuro = !!quota && quota.maxFiles > 0 && quota.fileCount >= quota.maxFiles;
  const spazioAlMuro = !!quota && quota.total > 0 && quota.used >= quota.total;

  // I DUE MURI NELLA PASTIGLIA (2026-09-13, sera).
  //
  // La fascia ambra dell'80% e' sparita: su un telefono da 640px di altezza
  // le cose fisse sopra l'elenco arrivavano al 55% dello schermo, e una
  // fascia intera per un promemoria e' il primo pezzo che si puo' togliere.
  // Il promemoria lo da' la pastiglia, che quei numeri li dice gia'
  // ("44/50 file"): all'80% diventa ambra, dal 95% rossa, e si colora la
  // meta' che si sta esaurendo — spazio o file — non tutte e due insieme.
  const pcSpazioMuro = quota && quota.total > 0 ? (quota.used / quota.total) * 100 : 0;
  const pcFileMuro = quota && quota.maxFiles > 0 ? (quota.fileCount / quota.maxFiles) * 100 : 0;
  const gradino = (pc) => (pc >= 95 ? 95 : pc >= 80 ? 80 : 0);
  const livSpazio = gradino(pcSpazioMuro);
  const livFile = gradino(pcFileMuro);
  const livelloMuro = Math.max(livSpazio, livFile);
  const tintaMuro = (liv) => (liv >= 95 ? '#B91C1C' : liv >= 80 ? '#B45309' : null);

  /** "1,0 GB" — una cifra decimale, la virgola dove si usa la virgola. */
  function pesoCorto(byte) {
    const virgola = i18n.language === 'en' ? '.' : ',';
    const gb = byte / (1024 * 1024 * 1024);
    if (gb >= 0.95) return gb.toFixed(1).replace('.', virgola) + ' ' + unitaByte(i18n.language, 'GB');
    const mb = byte / (1024 * 1024);
    if (mb >= 1) return mb.toFixed(1).replace('.', virgola) + ' ' + unitaByte(i18n.language, 'MB');
    return Math.round(byte / 1024) + ' ' + unitaByte(i18n.language, 'kB');
  }

  const muroGiaMostrato = useRef(false);
  useEffect(() => {
    if (muroGiaMostrato.current || !quota) return;
    const fileFiniti = quota.maxFiles > 0 && quota.fileCount >= quota.maxFiles;
    const spazioFinito = quota.total > 0 && quota.used >= quota.total;
    // Se lo stato e' tornato sotto la soglia, il ricordo si azzera: cosi' se un
    // domani si torna al muro il riquadro si rifa' vedere invece di restare
    // zitto perche' era gia' comparso una volta.
    if (!fileFiniti && !spazioFinito) { muroGiaMostrato.current = false; return; }
    muroGiaMostrato.current = true;
    setMuro({
      codice: fileFiniti ? 'FILE_LIMIT' : 'QUOTA_EXCEEDED',
      maxFiles: quota.maxFiles, fileCount: quota.fileCount,
      usato: pesoLeggibile(quota.used), totale: pesoLeggibile(quota.total),
      nonCaricati: 0,
    });
  }, [quota]);

  // AL RISVEGLIO SI RILEGGE (2026-09-14). Il caso: la notifica dice che sei
  // al muro, tocchi, l'app torna in primo piano e la pastiglia dice "29/50"
  // — il numero di quando l'app era stata aperta l'ultima volta. Quindi ogni
  // volta che la pagina torna visibile, non solo all'avvio, quota e conteggio
  // si rileggono. visibilitychange scatta nella WebView di Android a ogni
  // ritorno in primo piano e vale anche nel browser: una richiesta, e basta.
  //
  // PRIMA si rilegge, POI si decide se aprire il riquadro. L'avviso in
  // sospeso (il motivo che la notifica ha portato con se', consumato alla
  // lettura) si mostra solo se il muro c'e' ANCORA: se nel frattempo
  // l'utente ha fatto posto, dal computer o comprando un piano, l'avviso si
  // butta in silenzio. Dirti che sei al muro quando non lo sei e' peggio di
  // non dirti niente.
  useEffect(() => {
    let vivo = true;
    const risveglio = async () => {
      const q = await rileggiStato();
      if (!vivo || !Capacitor.isNativePlatform()) return;
      const avviso = await avvisoInSospesoDalTelefono();
      if (!vivo || !avviso || !q) return;
      // LA CHIAVE DI RECUPERO APRE LA CERIMONIA, NON IL RIQUADRO GENERICO
      // (2026-09-14). La notifica del backup che chiede la chiave porta il
      // codice RECOVERY_KEY_REQUIRED: se il server conferma che manca ancora,
      // si apre la stessa cerimonia del vault (fileInAttesa=[] la mostra senza
      // niente da caricare dopo). Se nel frattempo la chiave c'e', l'avviso e'
      // gia' stato consumato e non si mostra niente.
      if (avviso.codice === 'RECOVERY_KEY_REQUIRED') {
        if (!(await cerimoniaConfermataDalServer())) setFileInAttesa([]);
        return;
      }
      const fileFiniti = q.maxFiles > 0 && q.fileCount >= q.maxFiles;
      const spazioFinito = q.total > 0 && q.used >= q.total;
      if (avviso.codice === 'FILE_LIMIT' ? !fileFiniti : avviso.codice === 'QUOTA_EXCEEDED' ? !spazioFinito : !(fileFiniti || spazioFinito)) return;
      // I numeri sono quelli del server: preferisce quelli appena riletti,
      // e se mancano quelli che la notifica ha portato con se'.
      setMuro({
        codice: avviso.codice,
        maxFiles: q.maxFiles || avviso.maxFiles, fileCount: q.fileCount,
        usato: pesoLeggibile(q.used), totale: pesoLeggibile(q.total || avviso.quotaBytes),
        nonCaricati: 0,
        daNotifica: true,
      });
    };
    const suVisibilita = () => { if (document.visibilityState === 'visible') risveglio(); };
    risveglio();
    document.addEventListener('visibilitychange', suVisibilita);
    return () => { vivo = false; document.removeEventListener('visibilitychange', suVisibilita); };
  }, [rileggiStato]);

  // ── IL MOTORE ──────────────────────────────────────────────────────────
  const accendiMotore = useCallback(async () => {
    if (motoreAcceso.current) return;
    motoreAcceso.current = true;
    try {
      while (true) {
        // Si prende la prima in attesa. Le righe che aspettano una risposta
        // ("chiede") si saltano: rispondere e' dell'utente, e intanto gli
        // altri file non devono stare fermi.
        const riga = codaRef.current.find((r) => r.stato === 'attesa');
        if (!riga) break;

        const ctrl = new AbortController();
        annullatori.current.set(riga.id, ctrl);
        const misura = creaMisuratore();
        misuratori.current.set(riga.id, misura);
        aggiornaRiga(riga.id, { stato: 'in-corso', percento: riga.percento || 0 });
        try {
          if (riga.verso === 'giu') {
            await downloadFile(riga.fileId, riga.nome, {
              segnale: ctrl.signal,
              avanzamento: ({ scaricati, totale: tot }) => {
                // scaricati === null: il formato vecchio non sa dire a che
                // punto e'. La riga lo dichiara invece di mostrare uno zero
                // fermo, che sembrerebbe un blocco.
                if (scaricati == null) { aggiornaRiga(riga.id, { percento: null }); return; }
                misura.segna(scaricati);
                aggiornaRiga(riga.id, {
                  byteFatti: scaricati,
                  byteTotali: tot || riga.byteTotali,
                  percento: tot ? Math.min(100, Math.round((scaricati / tot) * 100)) : null,
                });
              },
            });
          } else {
            await uploadFile(riga.file, {
              encryptContent: true, encryptName: false,
              targetFolderId: riga.folderId,
              ripresa: riga.ripresa || null,
              segnale: ctrl.signal,
              // Appena la sessione si apre la riga se la segna: e' quello che
              // l'Annulla chiudera' sul server, e quello che la PAUSA
              // riprendera' senza rimandare i pezzi gia' saliti.
              onSessione: (sid) => aggiornaRiga(riga.id, { sessionId: sid, pausabile: true }),
              avanzamento: {
                stato: () => {},
                percento: (p) => aggiornaRiga(riga.id, { percento: p }),
                // 'assemblaggio': il server sta incollando i blocchi. La riga lo
                // dice, e non si puo' piu' mettere in pausa: i byte sono gia' tutti la'.
                fase: (f, posizione) => aggiornaRiga(riga.id, { fase: f, posizione, pausabile: false }),
                byte: (fatti, tot) => {
                  misura.segna(fatti);
                  aggiornaRiga(riga.id, { byteFatti: fatti, byteTotali: tot });
                },
              },
            });
          }
          aggiornaRiga(riga.id, { stato: 'fatto', percento: 100, byteFatti: riga.byteTotali, ripresa: null });
        } catch (e) {
          if (ctrl.signal.aborted) {
            // L'annullo o la pausa hanno gia' scritto la riga: l'uno ha chiuso
            // la sessione, l'altra l'ha lasciata aperta apposta.
          } else {
            const muroDiAccount = e.code === 'QUOTA_EXCEEDED' || e.code === 'FILE_LIMIT';
            aggiornaRiga(riga.id, {
              stato: muroDiAccount ? 'nonCaricato' : 'fallito',
              motivo: muroDiAccount ? null : formatUploadError(e),
              // Il sessionId arriva attaccato all'errore da caricaABlocchi: e'
              // quello che permette alla riprova di ripartire dai blocchi gia'
              // saliti invece di rifare tutto.
              sessionId: e.sessionId || riga.sessionId || null,
              riprendibile: !!(e.sessionId || riga.sessionId),
            });
            // ── I DUE RIFIUTI CHE RIGUARDANO L'ACCOUNT ──────────────────
            // Non e' andato storto QUESTO file: non ne passa piu' nessuno, e i
            // file dietro non ci proveranno nemmeno. Le loro righe diventano
            // "non caricato", che e' la verita', invece di "fallito", che
            // suggerirebbe un tentativo andato male.
            if (e.code === 'QUOTA_EXCEEDED' || e.code === 'FILE_LIMIT') {
              const inAttesa = codaRef.current.filter((r) => r.stato === 'attesa').length;
              scriviCoda((c) => c.map((r) => (r.stato === 'attesa'
                ? { ...r, stato: 'nonCaricato', motivo: null }
                : r)));
              setMuro({
                codice: e.code,
                maxFiles: e.maxFiles ?? quota?.maxFiles,
                fileCount: e.fileCount ?? quota?.fileCount,
                usato: pesoLeggibile(e.usedBytes ?? quota?.used ?? 0),
                totale: pesoLeggibile(e.quotaBytes ?? quota?.total ?? 0),
                // La riga che ha incontrato il muro non e' partita: si conta
                // anche lei fra quelle non caricate.
                nonCaricati: inAttesa + 1,
              });
            } else if (isBatchFatalUploadError(e)) {
              // Gli altri motivi che valgono per tutti (sessione scaduta):
              // restano righe fallite, con il loro messaggio.
              scriviCoda((c) => c.map((r) => (r.stato === 'attesa'
                ? { ...r, stato: 'fallito', motivo: formatUploadError(e), riprendibile: false }
                : r)));
            }
          }
        } finally {
          annullatori.current.delete(riga.id);
          misuratori.current.delete(riga.id);
        }
      }
    } finally {
      motoreAcceso.current = false;
    }
  }, [uploadFile, aggiornaRiga, scriviCoda]);

  // ── ACCODARE ───────────────────────────────────────────────────────────
  async function caricaDavvero(fl, opzioni = {}) {
    const { cartella = currentFolder, gruppoId = null } = opzioni;
    setUploadError('');
    const arr = Array.from(fl);
    if (!arr.length) return;
    if (!checkBatchFileLimit(arr.length + coda.filter((r) => r.stato !== 'fatto').length)) return;

    // Le sessioni lasciate a meta' si chiedono una volta per gruppo: serve a
    // capire quali file combaciano con qualcosa di gia' iniziato.
    let aperte = [];
    try { aperte = await elencaSessioniAperte(); } catch { aperte = []; }

    const nuove = arr.map((f) => {
      const id = 'r' + (++contatore.current);
      // Il file troppo grande non entra in coda come "da fare": nasce gia'
      // fallito, con la sua ragione. Prima fermava tutto il gruppo con un
      // banner in cima, e gli altri file non partivano nemmeno.
      const base = {
        id, file: f, nome: f.name, verso: 'su',
        byteTotali: f.size, byteFatti: 0, percento: 0,
        folderId: cartella, gruppoId: gruppoId || null,
        velocita: null, restano: null, pausabile: false,
      };
      const limite = quota?.maxFileSize;
      if (limite && limite > 0 && f.size > limite) {
        return { ...base, stato: 'fallito', riprendibile: false,
                 motivo: t('upload_troppo_grande', { dimensione: pesoLeggibile(f.size), limite: pesoLeggibile(limite) }) };
      }
      const candidata = sessioneCombaciante(f, aperte, cartella);
      if (candidata) {
        aperte = aperte.filter((x) => x.sessionId !== candidata.sessionId);
        return { ...base, stato: 'chiede', sessione: candidata };
      }
      return { ...base, stato: 'attesa' };
    });

    scriviCoda((c) => [...c, ...nuove]);
    accendiMotore();
  }

  // ── LE TRE AZIONI SULLE RIGHE ──────────────────────────────────────────
  function rispondiRiga(id, scelta) {
    const riga = codaRef.current.find((r) => r.id === id);
    if (!riga) return;
    if (scelta === 'ricomincia') {
      // La sessione vecchia va CHIUSA prima, non lasciata li': altrimenti
      // resta a combaciare col prossimo file e i suoi blocchi si ripropongono.
      abbandonaSessione(riga.sessione.sessionId);
      aggiornaRiga(id, { stato: 'attesa', sessione: null, ripresa: null });
    } else {
      aggiornaRiga(id, { stato: 'attesa', ripresa: riga.sessione });
    }
    accendiMotore();
  }

  async function riprovaRiga(id) {
    const riga = codaRef.current.find((r) => r.id === id);
    if (!riga) return;
    // Si rilegge dal server: i blocchi gia' saliti sono la verita' del disco,
    // non quello che il browser ricorda. Se la sessione non c'e' piu'
    // (scaduta, o non era a blocchi) si riparte da zero, e la riga lo mostra
    // perche' l'etichetta "riprende da" sparisce.
    let ripresa = null;
    if (riga.sessionId) {
      try { ripresa = (await sessioniAperte()).find((x) => x.sessionId === riga.sessionId) || null; } catch {}
    }
    // La percentuale riparte da dove il server dice, non da zero: altrimenti
    // la barra lampeggia a 0 e sembra che stia ricominciando tutto.
    const gia = ripresa && ripresa.totaleBlocchi
      ? Math.round((ripresa.arrivati / ripresa.totaleBlocchi) * 100) : 0;
    aggiornaRiga(id, { stato: 'attesa', motivo: null, percento: gia, ripresa,
                       riprendibile: !!ripresa, bloccoDa: ripresa ? ripresa.arrivati : 0 });
    accendiMotore();
  }

  /**
   * PAUSA. Esiste solo sulla strada a blocchi, e li' e' una pausa vera: si
   * smette di mandare pezzi, la sessione resta APERTA sul server e i blocchi
   * gia' saliti restano dove sono. Riprendere usa la stessa macchina della
   * ripresa dopo un guasto — quella provata: 42 blocchi su 75, zero sessioni
   * nuove.
   *
   * La differenza con Annulla e' tutta qui: l'annullo chiude la sessione
   * (DELETE), la pausa no.
   */
  function pausaRiga(id) {
    const ctrl = annullatori.current.get(id);
    aggiornaRiga(id, { stato: 'in-pausa', velocita: null, restano: null });
    if (ctrl) ctrl.abort();
  }

  async function riprendiRiga(id) {
    const riga = codaRef.current.find((r) => r.id === id);
    if (!riga) return;
    let ripresa = null;
    if (riga.sessionId) {
      try { ripresa = (await sessioniAperte()).find((x) => x.sessionId === riga.sessionId) || null; } catch {}
    }
    aggiornaRiga(id, { stato: 'attesa', ripresa });
    accendiMotore();
  }

  /**
   * Gli SCARICAMENTI entrano nella stessa coda dei caricamenti: una lista
   * sola. Non hanno pausa — in discesa non c'e' niente da tenere aperto, e
   * una pausa finta e' peggio di nessuna pausa.
   */
  function accodaScaricamento(file) {
    const id = 'r' + (++contatore.current);
    scriviCoda((c) => [...c, {
      id, fileId: file.id, nome: file.filename || file.plainName || t('tr_file_senza_nome'),
      verso: 'giu', byteTotali: file.size || 0, byteFatti: 0, percento: 0,
      stato: 'attesa', velocita: null, restano: null, pausabile: false,
    }]);
    accendiMotore();
  }

  async function annullaRiga(id) {
    const ctrl = annullatori.current.get(id);
    aggiornaRiga(id, { stato: 'annullato', percento: 0 });
    if (ctrl) ctrl.abort();
    // Fermare il browser non basta: i blocchi gia' saliti resterebbero sul
    // server per sette giorni. Si chiude la sessione — DELETE, non "chiudi",
    // che invece assemblerebbe il file e creerebbe la riga che l'utente ha
    // appena rifiutato.
    const riga = codaRef.current.find((r) => r.id === id);
    const sid = riga?.sessionId || riga?.ripresa?.sessionId;
    if (sid) { try { await abbandonaSessione(sid); } catch {} }
  }

  // ── TRASCINAMENTO DI UNA CARTELLA (2026-09-10) ──────────────────────────
  //
  // Prima il drop leggeva e.dataTransfer.files, e una cartella li' dentro non
  // c'e': Chrome ci mette un oggetto File che rappresenta la directory, e alla
  // prima lettura dei byte lancia "A requested file or directory could not be
  // found at the time an operation was processed." Quel messaggio non e'
  // nostro ne' di Windows: e' il testo che Blink usa per NotFoundError, e
  // finiva nel nostro banner rosso perche' formatUploadError stampa e.message
  // grezzo quando non riconosce un codice.
  //
  // Serve webkitGetAsEntry() e scendere nell'albero.

  // readEntries() restituisce AL MASSIMO 100 voci per chiamata e non lo dice:
  // va richiamata finche' non torna un lotto vuoto. Senza questo ciclo, una
  // cartella con 250 file ne caricherebbe 100 e sembrerebbe a posto.
  function leggiVoci(reader) {
    return new Promise((ok, ko) => {
      const tutte = [];
      const giro = () => reader.readEntries((lotto) => {
        if (!lotto.length) return ok(tutte);
        tutte.push(...lotto);
        giro();
      }, ko);
      giro();
    });
  }

  async function esploraAlbero(voce, percorsoPadre, acc) {
    if (voce.isFile) {
      const file = await new Promise((ok, ko) => voce.file(ok, ko));
      acc.file.push({ file, percorso: percorsoPadre });
      return;
    }
    const mio = [...percorsoPadre, voce.name];
    // Registrata SEMPRE, anche se non contiene niente: le cartelle vuote si
    // ricreano (deciso il 2026-09-10). Chi trascina un progetto con logs/ e
    // tmp/ vuote se le ritrova, e il comportamento coincide con quello del
    // pulsante "Cartella", che gia' fa cosi'.
    acc.cartelle.push(mio);
    for (const figlio of await leggiVoci(voce.createReader())) {
      await esploraAlbero(figlio, mio, acc);
    }
  }

  async function handleDropCartella(voci) {
    setUploadError('');
    // Stessa guardia del caricamento normale: la cerimonia della chiave non
    // si salta da nessuna porta.
    try {
      if (!(await cerimoniaConfermataDalServer())) { setFileInAttesa([]); return; }
    } catch { setUploadError(t('chiave_verifica_offline')); return; }

    setAvanzamentoCartella({ fatti: 0, totale: 0, nome: t('cartella_lettura') });
    const acc = { file: [], cartelle: [] };
    // Il nome della riga di gruppo: la cartella trascinata. Se ne sono state
    // trascinate piu' d'una si dice quello, invece di scegliere la prima e
    // far credere che siano tutte li' dentro.
    const cartelleTrascinate = voci.filter((v) => v.isDirectory);
    acc.nomeRadice = cartelleTrascinate.length === 1
      ? cartelleTrascinate[0].name
      : t('tr_cartelle_n', { count: cartelleTrascinate.length });
    try {
      for (const v of voci) await esploraAlbero(v, [], acc);
    } catch (e) {
      setAvanzamentoCartella(null);
      setUploadError(e.message);
      return;
    }

    if (!acc.file.length && !acc.cartelle.length) {
      setAvanzamentoCartella(null);
      setUploadError(t('cartella_nessun_file'));
      return;
    }
    if (!checkBatchFileLimit(acc.file.length)) { setAvanzamentoCartella(null); return; }

    // Le cartelle prima, dal livello piu' alto: un figlio non si crea senza
    // il padre. Ordinate per profondita', non per nome.
    const mappa = {};
    for (const p of acc.cartelle.slice().sort((a, b) => a.length - b.length)) {
      const padre = p.length > 1 ? mappa[p.slice(0, -1).join('/')] : currentFolder;
      try {
        const nuova = await createFolder(p[p.length - 1], padre ?? currentFolder);
        mappa[p.join('/')] = nuova.id;
      } catch {
        // Se la cartella non si crea, i suoi file finiscono nel padre invece
        // di essere persi. Stessa scelta di handleFolderUpload.
        mappa[p.join('/')] = padre ?? currentFolder;
      }
    }

    // I FILE VANNO NELLA CODA, non in un ciclo qui dentro (2026-09-13).
    //
    // Il ciclo che stava qui faceva la cosa giusta — uno alla volta, mai in
    // parallelo, perche' il tetto di richieste per utente e' basso e i
    // caricamenti paralleli producono 503 e frammenti a meta' — ma lo faceva
    // di nascosto: nessuna riga, nessun annulla, nessuna ripresa. La regola
    // "uno alla volta" adesso e' del motore della coda, che vale per tutti.
    const gruppoId = 'g' + (++contatore.current);
    const radiceNome = acc.nomeRadice || t('tr_cartella');
    scriviGruppi((gg) => [...gg, {
      id: gruppoId, nome: radiceNome,
      byteFatti: 0,
      byteTotali: acc.file.reduce((tot, x) => tot + (x.file.size || 0), 0),
    }]);

    const perCartella = new Map();
    for (const { file, percorso } of acc.file) {
      const dest = percorso.length ? mappa[percorso.join('/')] : currentFolder;
      if (!perCartella.has(dest)) perCartella.set(dest, []);
      perCartella.get(dest).push(file);
    }
    setAvanzamentoCartella(null);
    for (const [cartella, elenco] of perCartella) {
      await caricaDavvero(elenco, { cartella, gruppoId });
    }
    if (falliti.length) {
      const elenco = falliti.slice(0, 5).join(', ') + (falliti.length > 5 ? '…' : '');
      setUploadError(falliti.length === 1
        ? t('cartella_falliti_uno', { elenco })
        : t('cartella_falliti_molti', { quanti: falliti.length, elenco }));
    }
  }

  async function handleFolderUpload(e) {
    setUploadError('');
    const allFiles = Array.from(e.target.files);
    if (!allFiles.length) return;
    // Stessa regola del caricamento di file singoli: senza questa guardia la
    // cartella sarebbe una porta laterale che salta la cerimonia. Qui i file
    // non restano in coda (ricostruire l'albero delle cartelle a cerimonia
    // finita e' fragile): si apre lo schermo e l'utente riseleziona la
    // cartella dopo — il selettore e' un clic.
    try {
      if (!(await cerimoniaConfermataDalServer())) {
        setFileInAttesa([]);
        e.target.value = '';
        return;
      }
    } catch {
      setUploadError(t('chiave_verifica_offline'));
      e.target.value = '';
      return;
    }
    if (!checkBatchFileLimit(allFiles.length)) { e.target.value=''; return; }
    e.target.value = '';
    await accodaCartella(allFiles, (f) => f.webkitRelativePath);
  }

  /**
   * UNA CARTELLA DIVENTA UN GRUPPO NELLA CODA (2026-09-13).
   *
   * Prima c'era un ciclo tutto suo che caricava file per file, e la cartella
   * non compariva da nessuna parte: chi ne sceglieva una con trecento foto
   * vedeva solo la vecchia barra salire e scendere trecento volte.
   *
   * Adesso: si creano PRIMA le cartelle — servono gli id, e sono poche
   * richieste — poi i file entrano in coda tutti insieme con un gruppoId
   * comune. La riga di gruppo in cima dice nome, quanti file e a che punto e'
   * l'insieme.
   *
   * `percorsoDi` cambia fra il tasto (webkitRelativePath) e il trascinamento
   * (il percorso ricostruito scendendo nell'albero): il resto e' identico, ed
   * e' il motivo per cui questa funzione e' una sola.
   */
  async function accodaCartella(files, percorsoDi) {
    if (!files.length) return;
    const ordinati = [...files].sort((a, b) => percorsoDi(a).localeCompare(percorsoDi(b)));
    const radice = percorsoDi(ordinati[0]).split('/')[0] || t('tr_cartella');

    const mappaCartelle = {};
    const perFile = [];
    for (const file of ordinati) {
      const parti = percorsoDi(file).split('/');
      let genitore = currentFolder;
      for (let i = 0; i < parti.length - 1; i++) {
        const chiave = parti.slice(0, i + 1).join('/');
        if (!mappaCartelle[chiave]) {
          try { const nf = await createFolder(parti[i], genitore); mappaCartelle[chiave] = nf.id; }
          catch { mappaCartelle[chiave] = genitore; }
        }
        genitore = mappaCartelle[chiave];
      }
      perFile.push({ file, cartella: genitore });
    }

    const gruppoId = 'g' + (++contatore.current);
    scriviGruppi((gg) => [...gg, {
      id: gruppoId, nome: radice,
      byteFatti: 0,
      byteTotali: perFile.reduce((t, x) => t + x.file.size, 0),
    }]);

    // I file di una cartella possono finire in sottocartelle diverse: si
    // accodano a gruppetti, uno per cartella di destinazione, ma con lo stesso
    // gruppoId — cosi' restano un blocco solo davanti agli occhi.
    const perCartella = new Map();
    for (const x of perFile) {
      if (!perCartella.has(x.cartella)) perCartella.set(x.cartella, []);
      perCartella.get(x.cartella).push(x.file);
    }
    for (const [cartella, elenco] of perCartella) {
      await caricaDavvero(elenco, { cartella, gruppoId });
    }
  }

  // Download — passano dalla coda come i caricamenti: una lista sola, e si
  // vede a che punto sono invece di un tasto che gira e basta.
  function handleDownloadSingle(file) {
    accodaScaricamento(file);
  }
  function downloadSelected() {
    if (!selected.size) return;
    for (const file of viewFiles.filter(f => selected.has(f.id))) accodaScaricamento(file);
  }

  // Delete
  async function handleDeleteSingle(file) {
    if (!window.confirm(t('confirm_delete_file', {name: file.filename}))) return;
    setActionId(file.id+'x');
    try { await deleteFile(file.id); setSelected(s=>{const n=new Set(s);n.delete(file.id);return n;}); }
    catch(e) { alert(e.message); }
    finally { setActionId(null); }
  }
  async function deleteSelected() {
    if (!selected.size) return;
    if (!window.confirm(t('confirm_delete_selected', {count: selected.size}))) return;
    for (const id of selected) { try { await deleteFile(id); } catch {} }
    setSelected(new Set());
  }

  // Folder ops
  async function handleCreateteFolder() {
    setFolderError('');
    if (!newFolderName.trim()) return setFolderError(t('folder_name_required'));
    try { await createFolder(newFolderName.trim()); setNewFolderName(''); setShowNewFolder(false); await loadFiles(currentFolder); }
    catch(e) { setFolderError(e.message); }
  }
  async function handleRename(folder) {
    if (!renameValue.trim()) return;
    try { await renameFolder(folder.id,renameValue.trim()); setRenamingFolder(null); }
    catch(e) { alert(e.message); }
  }

  async function handleMoveFile(file, targetFolderId) {
    try {
      await moveFile(file.id, targetFolderId);
      setMoveModal(null);
    } catch(e) { alert(t('move_error_prefix', {message: e.message})); }
  }

  // Sposta TUTTI i selezionati, uno per uno, e dice com'e' andata per davvero
  // (2026-09-16). Nessun "riuscito" se ne e' arrivato uno su quattro: se uno
  // fallisce (file sparito, cartella sparita, rete) gli altri proseguono, e
  // alla fine il modale resta aperto con «N spostati, M no: nome (motivo)».
  // I file spostati escono dalla selezione; quelli non spostati ci restano,
  // cosi' si puo' riprovare subito con un'altra destinazione.
  async function handleMoveSelected(ids, targetFolderId) {
    setMoveError('');
    const nomi = new Map(viewFiles.map(f => [f.id, f.filename]));
    const falliti = [];            // testo per l'utente: «nome (motivo)»
    const fallitiId = new Set();   // per la selezione: chi resta selezionato
    let spostati = 0;
    for (const id of ids) {
      try { await moveFile(id, targetFolderId); spostati++; }
      catch (e) { falliti.push(`${nomi.get(id) || id} (${e.message})`); fallitiId.add(id); }
    }
    setSelected(prev => { const n = new Set(prev); for (const id of ids) if (!fallitiId.has(id)) n.delete(id); return n; });
    if (!falliti.length) { setMoveModal(null); return; }
    setMoveError(t('move_partial', { ok: spostati, ko: falliti.length, elenco: falliti.join('; ') }));
  }

  // Sposta una cartella con tutto il suo contenuto. L'errore si mostra DENTRO
  // il modale, non in un alert: il caso normale e' il nome gia' occupato
  // nella destinazione, e a quel punto serve poter scegliere un'altra
  // cartella senza riaprire tutto.
  async function handleMoveFolder(bersaglio, targetFolderId) {
    setMoveError('');
    try {
      await moveFolder(bersaglio.id, targetFolderId);
      setMoveModal(null);
    } catch(e) { setMoveError(e.message); }
  }

  // Replace file content — old content is kept in the file's version history
  async function handleReplaceFile(e) {
    const newFile = e.target.files[0];
    e.target.value = '';
    if (!newFile || !replaceTarget) return;
    setUploadError('');
    try { await replaceFile(replaceTarget.id, newFile, { encryptContent: true, encryptName: false }); }
    catch(err) { setUploadError(formatUploadError(err)+(err.code==='QUOTA_EXCEEDED'?t('upgrade_plan_suffix'):'')); }
    finally { setReplaceTarget(null); }
  }

  // Preview
  // Tetto per l'anteprima in memoria, per tipo e per dispositivo. Qui il file
  // si decifra TUTTO in RAM (picco ~2x il file), quindi sopra queste soglie
  // non si apre l'anteprima: si propone lo scaricamento, che scrive a blocchi
  // su disco e non riempie la memoria. limiteMiniatura() distingue gia' i
  // dispositivi con poca memoria (telefoni) da quelli con tanta. Questo chiude
  // anche il rischio del video enorme che faceva esplodere la scheda.
  function tettoAnteprima(ptype) {
    const MB = 1024 * 1024;
    const poca = limiteMiniatura() <= 8 * MB; // telefono / poca RAM
    switch (ptype) {
      case 'image': return poca ? 8 * MB : 25 * MB;
      case 'pdf':   return poca ? 20 * MB : 50 * MB;
      case 'text':  return 2 * MB;
      case 'video':
      case 'audio': return poca ? 40 * MB : 150 * MB;
      default:      return poca ? 20 * MB : 50 * MB;
    }
  }

  async function handlePreview(file) {
    const ptype = getPreviewType(file.filename);
    if (!ptype) { handleDownloadSingle(file); return; }
    setPreviewEditing(false); setPreviewDraft(''); setPreviewLoaded(''); setPreviewLoadOk(false);

    // Sopra il tetto non si decifra in memoria: si apre il modale gia' sul
    // messaggio "troppo grande" col tasto Scarica.
    if ((file.size || 0) > tettoAnteprima(ptype)) {
      setPreview({ url: null, filename: file.filename, type: ptype, file, troppoGrande: true });
      return;
    }

    setActionId(file.id+'p');

    // Le immagini idonee passano dalla cache condivisa con le miniature: se
    // la miniatura c'e' gia', aprirla a schermo intero e' istantaneo invece
    // di riscaricare e ridecifrare lo stesso file. Il blob resta di
    // proprieta' della cache, quindi non va revocato alla chiusura.
    if (ptype === 'image' && puoAvereMiniatura(file)) {
      setPreview({ url: null, filename: file.filename, type: ptype, file, daCache: true, caricando: true });
      try {
        const url = await urlImmagine(file, encKey);
        setPreview(p => (p && p.file?.id === file.id
          ? (url ? { ...p, url, caricando: false } : { ...p, caricando: false, errore: true })
          : p));
      } catch (e) {
        setPreview(p => (p && p.file?.id === file.id ? { ...p, caricando: false, errore: true } : p));
      } finally { setActionId(null); }
      return;
    }

    try {
      const { apiDownloadFile, marcaVersione } = await import('../utils/api');
      const { blob, isEncrypted } = await apiDownloadFile(file.id, marcaVersione(file));
      let finalBlob=blob, finalName=file.filename;
      if (isEncrypted) {
        // Due formati: v2 a blocchi (l'app e i file grandi) e v1 a IV singolo.
        // Si sceglie dalla RIGA (formatVersion), come lo scaricamento in
        // useFiles.downloadFile — mai dai primi byte, che in un v1 sono
        // cifrato casuale e possono valere "LCB2" per caso. Un v1 prende cosi'
        // il percorso di sempre. decryptFile non sa leggere il v2.
        if (Number(file.formatVersion) === 2) {
          const cif = await import('../utils/cifraturaBlocchi');
          finalBlob = await cif.decifraV2InMemoria(blob, encKey, file.mimeType || '');
        } else {
          const { decryptFile } = await import('../utils/crypto');
          const r = await decryptFile(encKey,blob,file.iv,file.encryptedName);
          finalBlob=r.plainBlob; finalName=r.filename||file.filename;
        }
      }
      const url=URL.createObjectURL(finalBlob);
      // Per i PDF si tiene anche il Blob: pdf.js legge i BYTE in memoria, non
      // una URL. Cosi' non esiste nemmeno un indirizzo da cui il documento
      // potrebbe essere richiesto — il blob serve solo al bottone "scarica".
      setPreview({url,filename:finalName,type:ptype,file,blob:ptype==='pdf'?finalBlob:null});
    } catch(e) {
      // Errore onesto: l'anteprima non si apre, ma il file non e' perso — si
      // scarica da un percorso indipendente (a blocchi). Mai "dati corrotti".
      setPreview({ url: null, filename: file.filename, type: ptype, file, errore: true });
    }
    finally { setActionId(null); }
  }
  function closePreview() {
    // Solo i blob creati QUI vanno revocati. Quelli che arrivano dalla cache
    // delle miniature appartengono alla cache, che li revoca da se' quando
    // escono dalle dieci: revocarli qui lascerebbe la miniatura rotta nella
    // lista sotto.
    if (preview && preview.url && !preview.daCache) URL.revokeObjectURL(preview.url);
    setPreview(null); setPreviewEditing(false); setPreviewDraft(''); setPreviewLoaded(''); setPreviewLoadOk(false);
  }

  // Swipe fra le foto sul telefono. Soglia a 45px e tolleranza verticale:
  // sotto quella e' un tocco, e uno scorrimento piu' verticale che
  // orizzontale e' l'utente che scorre, non che cambia foto.
  const touchImg = useRef(null);
  function onTouchStartImg(e) {
    const t0 = e.changedTouches?.[0];
    touchImg.current = t0 ? { x: t0.clientX, y: t0.clientY } : null;
  }
  function onTouchEndImg(e) {
    const p0 = touchImg.current; const t1 = e.changedTouches?.[0];
    touchImg.current = null;
    if (!p0 || !t1) return;
    const dx = t1.clientX - p0.x, dy = t1.clientY - p0.y;
    if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy)) return;
    vaiAImmagine(dx < 0 ? 1 : -1);
  }

  // ── Navigazione fra le immagini della cartella ───────────
  // Solo le immagini idonee, nello stesso ordine in cui sono elencate: le
  // frecce devono seguire quello che l'utente vede, non l'ordine di
  // caricamento.
  // sortedFiles viene ricostruito a ogni render, quindi memorizzarlo non
  // servirebbe a niente: il filtro diretto costa meno del confronto delle
  // dipendenze che lo proteggerebbe.
  const immaginiNavigabili = (sortedFiles || [])
    .filter(f => getPreviewType(f.filename) === 'image' && puoAvereMiniatura(f));
  function vaiAImmagine(passo) {
    if (!preview?.file || preview.type !== 'image') return;
    const i = immaginiNavigabili.findIndex(f => f.id === preview.file.id);
    if (i < 0) return;
    const succ = immaginiNavigabili[(i + passo + immaginiNavigabili.length) % immaginiNavigabili.length];
    if (succ && succ.id !== preview.file.id) handlePreview(succ);
  }

  // ── Search result navigation ─────────────────────────────
  function handleSearchOpenFolder(folderId) {
    setMobileTab('home');
    navigateToFolderId(folderId);
  }
  function handleSearchOpenFile(file) {
    setMobileTab('home');
    navigateToFolderId(file.folderId);
    handlePreview(file);
  }
  function startPreviewEdit() { setPreviewDraft(previewLoaded); setPreviewEditing(true); }
  function cancelPreviewEdit() { setPreviewEditing(false); }
  async function savePreviewEdit() {
    if (!preview?.file) return;
    setPreviewSaving(true);
    try {
      const newFile = new File([previewDraft], preview.filename, { type: preview.file.mimeType || 'text/plain' });
      await replaceFile(preview.file.id, newFile, { encryptContent: !!preview.file.encrypted, encryptName: false });
      URL.revokeObjectURL(preview.url);
      const newUrl = URL.createObjectURL(new Blob([previewDraft], { type: 'text/plain' }));
      setPreview(p => ({ ...p, url: newUrl }));
      setPreviewLoaded(previewDraft);
      setPreviewEditing(false);
    } catch(e) { alert(t('save_error_prefix', {message: e.message})); }
    finally { setPreviewSaving(false); }
  }

  // ── Context Menu builders ─────────────────────────────
  function closeCtx() { setCtxMenu(null); }

  function ctxOnBackground(e) {
    e.preventDefault();
    setCtxMenu({ x:e.clientX, y:e.clientY, items:[
      { icon:'📁', label:t('menu_new_folder'), action:()=>{ setNewFolderName(''); setFolderError(''); setShowNewFolder(true); } },
      { icon:'⬆', label:t('menu_upload_file'), action:()=>fileInputRef.current.click() },
      // La voce compare solo dove una cartella si puo' davvero scegliere. Su
      // telefono nessun browser lo permette: webkitdirectory viene ignorato e
      // si apre il selettore dei FILE, che sembra un difetto nostro.
      ...(canPickDirectory
        ? [{ icon:'📂', label:t('menu_upload_folder'), action:()=>folderInputRef.current.click() }]
        : []),
      { sep:true },
      { icon:'↻', label:t('menu_refresh'), action:()=>loadFiles(currentFolder) },
    ]});
  }

  // Reached either via right-click (desktop) or a touch long-press, which
  // fires this same contextmenu event on mobile browsers — both open the
  // identical menu. Every row/card also has a "⋯" button calling this same
  // function via a plain click, as a second, always-discoverable way in.
  function ctxOnFolder(e, folder) {
    e.preventDefault(); e.stopPropagation();
    setCtxMenu({ x:e.clientX, y:e.clientY, items:[
      { icon:'→', label:t('menu_open'), action:()=>openFolder(folder) },
      { icon:'🔗', label:t('menu_share'), action:()=>setShareTarget({type:'folder', id:folder.id, name:folder.name, raw:folder}) },
      { sep:true },
      { icon:'✏', label:t('menu_rename'), action:()=>{ setRenamingFolder(folder.id); setRenameValue(folder.name); } },
      // Spostare le cartelle mancava del tutto (2026-09-06): si potevano
      // spostare i file, uno alla volta, ma non le cartelle. Chi si ritrovava
      // un albero nel posto sbagliato non aveva alcun modo di rimetterlo a
      // posto se non rifacendo tutto.
      { icon:'📂', label:t('menu_move_to_folder'), action:()=>{ setMoveError(''); setMoveModal({ tipo:'cartella', id:folder.id, nome:folder.name }); } },
      { icon:'📁', label:t('menu_new_subfolder'), action:()=>{ setNewFolderName(''); setFolderError(''); setShowNewFolder(true); } },
      { sep:true },
      { icon:'🗑', label:t('menu_delete_folder'), danger:true, action:()=>{ if(window.confirm(t('confirm_delete_folder', {name: folder.name}))) deleteFolder(folder.id); } },
    ]});
  }

  function ctxOnFile(e, file) {
    e.preventDefault(); e.stopPropagation();
    const canPreview = !!getPreviewType(file.filename);
    setCtxMenu({ x:e.clientX, y:e.clientY, items:[
      ...(canPreview ? [{ icon:'👁', label:t('menu_preview'), action:()=>handlePreview(file) }] : []),
      { icon:'⬇', label:t('menu_download'), action:()=>handleDownloadSingle(file) },
      { icon:'🔗', label:t('menu_share'), action:()=>setShareTarget({type:'file', id:file.id, name:file.filename, raw:file}) },
      { sep:true },
      { icon:'📂', label:t('menu_move_to_folder'), action:()=>{ setMoveError(''); setMoveModal({ tipo:'file', id:file.id, nome:file.filename, raw:file }); } },
      { icon:'🔁', label:t('menu_replace_file'), action:()=>{ setReplaceTarget(file); replaceInputRef.current.click(); } },
      // La voce dice cosa si sta per fare, non come si chiama la cosa: chi non
      // ha ancora una didascalia legge «Scrivi», chi ce l'ha legge «Modifica».
      { icon:'💬', label:t(file.didascaliaTesto ? 'menu_didascalia_modifica' : 'menu_didascalia_scrivi'),
        action:()=>{ setDidascaliaTesto(file.didascaliaTesto || ''); setDidascaliaErrore(''); setDidascaliaTarget(file); } },
      { icon:file.preferitoAt ? '★' : '☆', label:t(file.preferitoAt ? 'menu_preferito_togli' : 'menu_preferito_metti'),
        action:()=>cambiaPreferito(file) },
      { icon:'🕘', label:t('menu_version_history'), action:()=>setVersionsTarget(file) },
      { icon:'☑', label:selected.has(file.id)?t('menu_deselect'):t('menu_select'), action:()=>{ const e={stopPropagation:()=>{}}; toggleSelect(file.id,e); } },
      ...(selected.size > 1 ? [
        { icon:'⬇', label:t('menu_download_selected', {count: selected.size}), action:downloadSelected },
        // SPOSTA N SELEZIONATI (2026-09-16): prima la voce «Sposta nella
        // cartella…» agiva sempre e solo sul file cliccato — con 4 selezionati
        // ne arrivava 1 e gli altri 3 restavano dov'erano. Ora, come le due
        // voci sopra, con selezione multipla c'e' la voce con il conteggio che
        // sposta l'insieme dei selezionati.
        { icon:'📂', label:t('menu_move_selected', {count: selected.size}), action:()=>{ setMoveError(''); setMoveModal({ tipo:'file-multi', ids:[...selected], nome:t('move_n_files', {count: selected.size}) }); } },
        { icon:'🗑', label:t('menu_delete_selected', {count: selected.size}), danger:true, action:deleteSelected },
      ] : []),
      { sep:true },
      { icon:'🗑', label:t('menu_delete_file'), danger:true, action:()=>handleDeleteSingle(file) },
    ]});
  }

  // Uscendo dal vault (logout, blocco, cambio pagina) i blob decifrati non
  // devono restare in memoria: e' contenuto in chiaro, e la sessione e'
  // finita. La cache si svuota da se' oltre le dieci immagini, ma qui si
  // butta via tutto insieme.
  useEffect(() => () => { svuotaMiniature(); svuotaMemoriaDidascalie(); }, []);

  // Al montaggio del vault si porta lo scorrimento in cima, e si spegne il
  // ripristino automatico del browser (2026-09-04). Su mobile, al primo
  // accesso, il browser riapriva la pagina in una posizione scorsa da una
  // sessione precedente, e il vault si apriva a meta'. La fascia dell'offerta
  // in cima che aggravava il difetto non esiste piu' (tolta il 2026-09-06),
  // ma questo resta: garantisce che il vault si apra sempre dall'alto.
  useEffect(() => {
    let precedente;
    try {
      precedente = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
    } catch { /* non supportato: si procede col solo scrollTo */ }
    window.scrollTo(0, 0);
    return () => { try { if (precedente) window.history.scrollRestoration = precedente; } catch {} };
  }, []);

  // Con il visore aperto la pagina sotto non deve scorrere. Il CSS da solo
  // non basta: su iOS il corpo continua a muoversi dietro l'overlay mentre
  // si trascina l'immagine. Si blocca il body finche' il visore e' aperto e
  // si ripristina esattamente il valore precedente, senza darlo per scontato.
  useEffect(() => {
    if (!preview) return undefined;
    const prima = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prima; };
  }, [preview]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if (e.key==='Escape') {
        if (previewEditing) { setPreviewEditing(false); return; }
        setCtxMenu(null); closePreview(); setShowNewFolder(false);
      }
      if (preview?.type==='image') {
        if (e.key==='ArrowLeft')  { e.preventDefault(); vaiAImmagine(-1); return; }
        if (e.key==='ArrowRight') { e.preventDefault(); vaiAImmagine(1);  return; }
      }
      if (e.key==='Delete' && hasSelection) deleteSelected();
      if ((e.ctrlKey||e.metaKey) && e.key==='a') { e.preventDefault(); selectAll(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasSelection, selected, previewEditing, preview]); // eslint-disable-line

  function SortIcon({col}) {
    if (sortBy!==col) return <span className="vz-sort-icon">↕</span>;
    return <span className="vz-sort-icon active">{sortDir==='asc'?'↑':'↓'}</span>;
  }

  // Intestazione di colonna che si comporta da pulsante: mano, sfondo al
  // passaggio, raggiungibile con Tab, si attiva con Invio o barra spaziatrice,
  // e dice agli screen reader in che verso e' ordinata.
  function TestaOrdinabile({ col, classe, children }) {
    const attiva = sortBy === col;
    return (
      <div
        className={`${classe} vz-sortable${attiva ? ' vz-sortable-attiva' : ''}`}
        role="columnheader"
        tabIndex={0}
        aria-sort={attiva ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        title={t('ordina_per', { colonna: col === 'name' ? t('col_name') : col === 'date' ? t('col_date') : t('col_size') })}
        onClick={() => toggleSort(col)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(col); } }}
      >
        {children} <SortIcon col={col}/>
      </div>
    );
  }

  // SU TELEFONO le colonne data e dimensione sono nascoste dal CSS, quindi
  // le loro intestazioni non esistono: si poteva ordinare SOLO per nome, e
  // una volta fatto non si tornava piu' alla data — che e' l'ordinamento
  // predefinito. Non era un controllo poco visibile, era un controllo
  // assente. Questa barra compare solo dove le colonne non ci sono.
  function BarraOrdinamentoMobile() {
    const voci = [['name', t('col_name')], ['date', t('col_date')], ['size', t('col_size')]];
    return (
      <div className="vz-ordina-mobile" role="group" aria-label={t('ordina_titolo')}>
        <span className="vz-ordina-etichetta">{t('ordina_titolo')}</span>
        {voci.map(([col, testo]) => (
          <button
            key={col}
            type="button"
            className={`vz-ordina-voce${sortBy === col ? ' attiva' : ''}`}
            aria-pressed={sortBy === col}
            onClick={() => toggleSort(col)}
          >
            {testo}{sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
        ))}
      </div>
    );
  }

  const totalItems = viewFolders.length + viewFiles.length;

  // Classi di stato lette dal CSS (2026-09-05): vz-pagante = piano a
  // pagamento (per lui l'offerta e' nascosta, quindi su mobile la barra
  // "Carica file" scende ad ancorarsi sopra la dock invece di galleggiare nel
  // vuoto lasciato dall'offerta); vz-vuoto = vault senza file ne' cartelle,
  // dove la barra ancorata NON deve esistere affatto: c'e' gia' il blocco
  // centrato col suo pulsante.
  return (
    <>
    <AvvisoAccount motivo={muro} onChiudi={() => setMuro(null)}
                   onVediPiani={() => { setMuro(null); onUpgrade(); }} />
    <Trasferimenti
      righe={coda}
      gruppi={gruppi}
      totale={totale}
      onRiprova={riprovaRiga}
      onAnnulla={annullaRiga}
      onPausa={pausaRiga}
      onRiprendi={riprendiRiga}
      onScegli={rispondiRiga}
      onTogliRiga={(id) => scriviCoda((c) => c.filter((r) => r.id !== id))}
      onChiudi={() => { scriviCoda([]); scriviGruppi([]); }}
    />
    <div className={`vz-root${planId !== 'free' ? ' vz-pagante' : ''}${vaultDelTuttoVuoto ? ' vz-vuoto' : ''}${planId === 'free' ? ' vz-con-banner' : ''}`} style={{position:"relative"}} onClick={()=>setCtxMenu(null)}>
      {/* OFFERTA LAMPO (2026-09-07): la stessa barra della home, in cima a
          tutto e fissa mentre si scorre, per i soli gratuiti. Stesso
          componente e stesso timer degli altri due posti — il conto e'
          identico ovunque perche' la scadenza sta in un posto solo.
          La classe vz-con-banner sul contenitore fa scendere l'intestazione
          del file manager di quanto e' alta la barra: senza, la barra
          coprirebbe il logo e la barra di ricerca. */}
      {/* LA STRISCIA OFFERTA NON STA PIU' IN CIMA (2026-09-16): si e' spostata
          agganciata in fondo, sopra "Carica file" (posto="fondo", piu' in
          basso in questo file). In cima non ne resta nessuna: --barre-fisse
          cala da se' perche' non si registra piu' niente qui. Dentro l'app non
          si mostra affatto (upsell): il gate !dentroApp() e' sul posto nuovo. */}
      {ctxMenu && <ContextMenu x={ctxMenu.x} y={ctxMenu.y} items={ctxMenu.items} onClose={closeCtx}/>}

      {/* Recovery Modal */}
      {newRecoveryToken && (
        <div className="vz-modal-overlay">
          <div className="vz-modal" style={{maxWidth:500}} onClick={e=>e.stopPropagation()}>
            <h3 className="vz-modal-title">🔑 {t('recovery_title')}</h3>
            <div className="auth-notice" style={{margin:'4px 0'}}>
              <span>🛡️</span>
              <p>{t('recovery_encryption_notice_p1')} <strong>{t('recovery_encryption_notice_bold1')}</strong></p>
            </div>
            <div className="rt-alert" style={{margin:'4px 0'}}>⚠️ <strong>{t('recovery_warning')}</strong></div>
            <div className="rt-box" style={{margin:'8px 0'}}>
              <div className="rt-token">{newRecoveryToken}</div>
              <button className={`rt-copy ${recoveryCopied?'copied':''}`} onClick={copyRecoveryToken}>
                {recoveryCopied ? t('recovery_copied_btn') : t('recovery_copy_btn')}
              </button>
            </div>
            <div style={{display:'flex',gap:10,marginTop:8}}>
              <button className="vz-btn vz-btn-primary" onClick={downloadRecoveryFile} style={{flex:2}}>⬇ {t('recovery_download_btn')}</button>
              <button className={`vz-btn ${(recoveryDlDone||recoveryCopied)?'vz-btn-primary':'vz-btn-ghost'}`} onClick={handleSkipRecovery} style={{flex:1}}>
                {(recoveryDlDone||recoveryCopied)?`✓ ${t('recovery_continue_btn')}`:t('recovery_skip_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolder && (
        <div className="vz-modal-overlay" onClick={()=>setShowNewFolder(false)}>
          <div className="vz-modal" onClick={e=>e.stopPropagation()}>
            <h3 className="vz-modal-title">📁 {t('new_folder_title')}</h3>
            <input autoFocus className="vz-modal-input" type="text" value={newFolderName}
              onChange={e=>setNewFolderName(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleCreateteFolder()}
              placeholder={t('new_folder_placeholder')}/>
            {folderError && <p className="vz-modal-err">{folderError}</p>}
            <div className="vz-modal-btns">
              <button className="vz-btn vz-btn-ghost" onClick={()=>setShowNewFolder(false)}>{t('btn_cancel')}</button>
              <button className="vz-btn vz-btn-primary" onClick={handleCreateteFolder}>{t('btn_create')}</button>
            </div>
          </div>
        </div>
      )}

      {didascaliaTarget && (
        <div className="vz-modal-overlay" onClick={()=>!didascaliaBusy && setDidascaliaTarget(null)}>
          <div className="vz-modal" onClick={e=>e.stopPropagation()}>
            <h3 className="vz-modal-title">💬 {t('didascalia_titolo')}</h3>
            <p className="vz-didascalia-file" title={didascaliaTarget.filename}>{didascaliaTarget.filename}</p>
            <textarea autoFocus className="vz-modal-input vz-didascalia-campo" maxLength={200}
              value={didascaliaTesto}
              onChange={e=>setDidascaliaTesto(e.target.value.slice(0,200))}
              placeholder={t('didascalia_segnaposto')}/>
            <p className="vz-didascalia-conta">{didascaliaTesto.length}/200 · {t('didascalia_aiuto')}</p>
            {didascaliaErrore && <p className="vz-modal-err">{didascaliaErrore}</p>}
            <div className="vz-modal-btns">
              <button className="vz-btn vz-btn-ghost" onClick={()=>setDidascaliaTarget(null)} disabled={didascaliaBusy}>{t('btn_cancel')}</button>
              <button className="vz-btn vz-btn-primary" onClick={salvaLaDidascalia} disabled={didascaliaBusy}>
                {didascaliaBusy ? t('didascalia_salvataggio') : t('didascalia_salva')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {/* Cerimonia della chiave: compare quando l'utente sceglie i primi file
          e non ha ancora salvato la sua chiave. I file restano in attesa e
          partono da soli appena la cerimonia e' completata. */}
      {fileInAttesa !== null && (
        <CerimoniaChiave
          nomeFile={fileInAttesa?.length === 1 ? fileInAttesa[0].name : null}
          onAnnulla={() => setFileInAttesa(null)}
          onFatto={() => {
            const daCaricare = fileInAttesa;
            setFileInAttesa(null);
            if (daCaricare?.length) caricaDavvero(daCaricare);
          }}
          onContinuaSenzaChiudere={() => {
            // Clipboard negata: il caricamento parte LO STESSO, ma la
            // schermata resta aperta (fileInAttesa non si azzera) cosi'
            // l'utente puo' copiare la chiave a mano. La chiudera' lui.
            const daCaricare = fileInAttesa;
            if (daCaricare?.length) caricaDavvero(daCaricare);
          }}
        />
      )}

      {preview && (
        <div className="pv-overlay" onClick={closePreview}>
          <div className="pv-modal" onClick={e=>e.stopPropagation()}>
            <div className="pv-header">
              <span className="pv-filename">{preview.filename}</span>
              <div className="pv-header-actions">
                {preview.type!=='image' && preview.type==='text' && !previewEditing && previewLoadOk && (
                  <button className="pv-btn-dl" onClick={startPreviewEdit}>✏ {t('preview_edit_btn')}</button>
                )}
                {preview.type==='text' && previewEditing && (
                  <>
                    <button className="pv-btn-dl" onClick={cancelPreviewEdit} disabled={previewSaving}>✕ {t('preview_cancel_btn')}</button>
                    <button className="pv-btn-dl" onClick={savePreviewEdit} disabled={previewSaving}>{previewSaving?`⏳ ${t('preview_saving_btn')}`:`💾 ${t('preview_save_btn')}`}</button>
                  </>
                )}
                {preview.url && (
                  <a className="pv-btn-dl" href={preview.url} download={preview.filename}>⬇ {t('preview_download_btn')}</a>
                )}
                <button className="pv-btn-close" onClick={closePreview}>✕</button>
              </div>
            </div>
            <div className="pv-content">
              {(preview.troppoGrande || preview.errore) ? (
                /* Difetto o limite nostro: mai un'immagine rotta, mai "dati
                   corrotti". Un messaggio onesto e il tasto per scaricare, che
                   funziona da un percorso indipendente dall'anteprima. */
                <div className="pv-loading" role="status" aria-live="polite">
                  <div aria-hidden="true" style={{fontSize:40,lineHeight:1}}>{preview.troppoGrande ? '📦' : '🔒'}</div>
                  <p style={{margin:0,maxWidth:360,textAlign:'center'}}>{preview.troppoGrande ? t('preview_too_big') : t('preview_unavailable')}</p>
                  <button className="pv-btn-dl" onClick={()=>{ handleDownloadSingle(preview.file); closePreview(); }}>
                    ⬇ {t('preview_download_btn')}
                  </button>
                </div>
              ) : (<>
              {preview.type==='image' && (
                <>
                  {/* Mentre decifra: su un'immagine grande ci mette qualche
                      secondo, e senza indicazione sembra bloccata. */}
                  {preview.caricando && (
                    <div className="pv-loading" role="status" aria-live="polite">
                      <div className="pv-spinner" aria-hidden="true" />
                      <span>{t('preview_decrypting')}</span>
                    </div>
                  )}
                  {preview.errore && <div className="pv-loading">{t('preview_failed')}</div>}
                  {preview.url && (
                    <img src={preview.url} alt={preview.filename} className="pv-image"
                         draggable={false}
                         onTouchStart={onTouchStartImg} onTouchEnd={onTouchEndImg}/>
                  )}
                  {immaginiNavigabili.length > 1 && (
                    <>
                      <button className="pv-nav pv-nav-prev" aria-label={t('preview_prev')}
                              onClick={e=>{e.stopPropagation();vaiAImmagine(-1);}}>‹</button>
                      <button className="pv-nav pv-nav-next" aria-label={t('preview_next')}
                              onClick={e=>{e.stopPropagation();vaiAImmagine(1);}}>›</button>
                    </>
                  )}
                </>
              )}
              {preview.type==='video' && <video src={preview.url} controls autoPlay className="pv-video"/>}
              {preview.type==='audio' && <div className="pv-audio-wrap"><div className="pv-audio-icon">🎵</div><p className="pv-audio-name">{preview.filename}</p><audio src={preview.url} controls autoPlay className="pv-audio"/></div>}
              {/* L'IFRAME NON C'E' PIU' (2026-09-12). <iframe src=blob> si
                  appoggia al lettore PDF del browser: su un desktop c'e',
                  dentro la WebView di Android no — e quando non c'e' non
                  da' errore, da' una pagina bianca. Ora il PDF lo disegna
                  pdf.js, che e' nostro e sta sul nostro dominio. */}
              {preview.type==='pdf' && (
                <Suspense fallback={(
                  <div className="pv-loading" role="status" aria-live="polite">
                    <div className="pv-spinner" aria-hidden="true" />
                    <span>{t('pdf_apertura')}</span>
                  </div>
                )}>
                  <PdfPreview blob={preview.blob} filename={preview.filename} urlScarico={preview.url} />
                </Suspense>
              )}
              {preview.type==='text' && (
                <TextPreview url={preview.url} filename={preview.filename}
                  editing={previewEditing} draft={previewDraft}
                  onDraftChange={setPreviewDraft}
                  onLoaded={t=>{ setPreviewLoaded(t); setPreviewLoadOk(true); }}
                  onLoadError={()=>setPreviewLoadOk(false)}/>
              )}
              </>)}
            </div>
          </div>
        </div>
      )}


      {/* Sposta — vale sia per un file sia per una CARTELLA (2026-09-06).
          Per una cartella l'elenco delle destinazioni toglie la cartella
          stessa e tutte le sue discendenti: metterla dentro se' stessa la
          farebbe sparire dall'albero pur restando nel database. Il server
          rifiuta comunque (vedi routes/files.js), ma un'opzione che non si
          puo' scegliere e' meglio di un errore da leggere. */}
      {moveModal && (() => {
        const vietate = new Set();
        if (moveModal.tipo === 'cartella') {
          vietate.add(moveModal.id);
          let cresciuto = true;
          while (cresciuto) {
            cresciuto = false;
            for (const f of allFolders) {
              if (!vietate.has(f.id) && f.parentId && vietate.has(f.parentId)) { vietate.add(f.id); cresciuto = true; }
            }
          }
        }
        const destinazioni = allFolders.filter(f => !vietate.has(f.id));
        const sposta = (dove) => moveModal.tipo === 'cartella'
          ? handleMoveFolder(moveModal, dove)
          : moveModal.tipo === 'file-multi'
            ? handleMoveSelected(moveModal.ids, dove)
            : handleMoveFile(moveModal.raw, dove);
        return (
        <div className="vz-modal-overlay" onClick={()=>setMoveModal(null)}>
          <div className="vz-modal" style={{maxWidth:420}} onClick={e=>e.stopPropagation()}>
            <h3 className="vz-modal-title">📂 {moveModal.tipo === 'cartella' ? t('move_title_folder') : moveModal.tipo === 'file-multi' ? t('move_title_files', { count: moveModal.ids.length }) : t('move_title_file')}</h3>
            <p style={{fontSize:13,color:'var(--text3)',marginBottom:8}}>
              {t('move_choose_where')} <strong style={{color:'var(--text)'}}>{moveModal.nome}</strong>
            </p>
            {moveError && <p style={{fontSize:13,color:'var(--red,#ef4444)',marginBottom:8}}>{moveError}</p>}
            <div className="move-folder-list">
              <button className="move-folder-item" onClick={()=>sposta(null)}>
                <span>{t('move_vault_root')}</span>
              </button>
              {destinazioni.map(f => (
                <button key={f.id} className={`move-folder-item ${f.id===currentFolder?'move-folder-current':''}`}
                  onClick={()=>sposta(f.id)}>
                  <FolderIcon name={f.name} size={16}/>
                  <span>{f.name}</span>
                  {f.id === currentFolder && <span style={{fontSize:10,color:'var(--text3)',marginLeft:'auto'}}>{t('move_current_folder')}</span>}
                </button>
              ))}
            </div>
            <div className="vz-modal-btns">
              <button className="vz-btn vz-btn-ghost" onClick={()=>setMoveModal(null)}>{t('btn_cancel')}</button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* ── HEADER (unified: brand + breadcrumb + all vault actions) ── */}
      <header className={`vz-header${listaScorsa ? ' vz-header-scorsa' : ''}`} ref={headerRef}>
        <div className="vz-header-left">
          <button className="vz-sidebar-toggle" onClick={()=>setSidebarOpen(o=>!o)} title={t('sidebar_toggle_title')}>☰</button>
          <div className="vz-logo"><span className="vz-logo-text">LifetimeCloud</span></div>
        </div>

        <div className="vz-header-crumb">
          {isRecentView ? (
            <div className="vz-breadcrumb"><span className="vz-bc" style={{cursor:'default'}}>🕐 {t('recent_files_label')}</span></div>
          ) : isPreferitiView ? (
            <div className="vz-breadcrumb"><span className="vz-bc" style={{cursor:'default'}}>★ {t('preferiti_label')}</span></div>
          ) : (
            <div className="vz-breadcrumb">
              <button className="vz-bc" onClick={()=>navigateTo(null,-1)}>{t('vault_home')}</button>
              {folderPath.map((f,i)=>(
                <span key={f.id} className="vz-bc-group">
                  <span className="vz-bc-sep">/</span>
                  <button className="vz-bc" onClick={()=>navigateTo(f.id,i)} title={f.name}>
                    <span className="vz-bc-name">{f.name}</span>
                  </button>
                </span>
              ))}
            </div>
          )}
          {/* CERCA SALE NELLA BARRA (2026-09-19). Sul desktop la pillola
              galleggiante spariva sotto i file e conteneva cinque voci che
              esistevano gia' nel menu dell'account: profilo, persone,
              assistenza, piani, esci. L'unica che non stava da nessun'altra
              parte era la ricerca, e in un gestore di file il posto della
              ricerca e' accanto al percorso. Stessa azione di prima, lo
              stesso pannello: non e' una ricerca nuova.
              SOLO DA DESKTOP: su telefono la pillola in basso resta la
              navigazione e la ricerca resta la sua prima voce (App.css). */}
          <button
            type="button"
            className={`vz-cerca-btn${searchOpen ? ' aperto' : ''}`}
            onClick={()=>toggleFlyout('search')}
            aria-expanded={searchOpen}
            title={t('dock_search')}
            aria-label={t('dock_search')}
          >
            <IconSearch width={15} height={15}/>
            <span className="vz-cerca-testo">{t('dock_search')}</span>
          </button>
        </div>

        {/* I due riquadri d'acquisto 1TB/2TB sono stati TOLTI (2026-09-16):
            erano un percorso d'acquisto non gated per l'app (falla Play) e
            sono stati sostituiti dalla striscia offerta agganciata in fondo
            (posto="fondo", sotto), dentro !dentroApp(). */}

        <div className="vz-header-actions">
          {/* VEDI I NOSTRI PIANI (2026-09-19). Sta fra la ricerca e il
              selettore di vista, contornato e non pieno: il pieno e' di
              "Carica", che resta l'azione principale della barra.
              LO VEDE SOLO CHI E' SUL GRATUITO: il piano si legge da
              user.plan (planId, qui sopra), e chi ha gia' comprato a vita
              non si vede proporre un acquisto.
              Su telefono non compare affatto: li' la barra non ha spazio e
              "Piani" sta gia' nella barra in basso. Il testo si accorcia da
              solo prima che la barra si affolli — vedi App.css. */}
          {planId === 'free' && (
            <button
              type="button"
              className="vz-piani-btn"
              onClick={onUpgrade}
              title={t('header_vedi_nostri_piani')}
              aria-label={t('header_vedi_nostri_piani')}
            >
              <span className="vz-piani-icona" aria-hidden="true"><IconPlans/></span>
              <span className="vz-piani-lungo">{t('header_vedi_nostri_piani')}</span>
              <span className="vz-piani-corto">{t('spazio_vedi_piani')}</span>
              <span className="vz-piani-minimo">{t('dock_plans')}</span>
            </button>
          )}
          <div className="vz-view-toggle">
            <button className={viewMode==='list'?'active':''} onClick={()=>setViewMode('list')} title={t('list_view_title')} aria-label={t('list_view_label')}>
              <span className="vz-vt-icon">≡</span><span className="vz-vt-label">{t('list_view_label')}</span>
            </button>
            <button className={viewMode==='grid'?'active':''} onClick={()=>setViewMode('grid')} title={t('grid_view_title')} aria-label={t('grid_view_label')}>
              <span className="vz-vt-icon">⊞</span><span className="vz-vt-label">{t('grid_view_label')}</span>
            </button>
          </div>

          {hasSelection && (
            <div className="vz-selection-bar">
              <span className="vz-sel-count">{t('selection_count', {count: selected.size})}</span>
              <button className="vz-btn vz-btn-sm vz-btn-blue" onClick={downloadSelected} disabled={downloading}>{downloading?'⏳':'⬇'} {t('btn_download')}</button>
              <button className="vz-btn vz-btn-sm vz-btn-red" onClick={deleteSelected}>🗑 {t('btn_delete')}</button>
              <button className="vz-btn vz-btn-sm vz-btn-ghost" onClick={()=>setSelected(new Set())}>✕</button>
            </div>
          )}
          {!vistaSpeciale && <>
            {/* L'etichetta va in uno span perche' la barra, stringendosi,
                possa cederla e lasciare la sola icona. Nome e suggerimento
                restano sul pulsante: chi lo tocca con la tastiera o lo legge
                con la voce sente sempre come si chiama. */}
            <button className="vz-btn vz-btn-ghost vz-btn-sm vz-header-folder-btn" onClick={()=>folderInputRef.current.click()}
                    title={t('btn_folder')} aria-label={t('btn_folder')}>
              <span className="vz-btn-icona" aria-hidden="true">📁</span>
              <span className="vz-btn-testo">{t('btn_folder')}</span>
            </button>
            <button className="vz-btn vz-btn-primary vz-btn-sm vz-header-upload-btn" onClick={()=>fileInputRef.current.click()}
                    title={t('btn_upload')} aria-label={t('btn_upload')}>
              <span className="vz-btn-icona" aria-hidden="true"><IconUpload width={15} height={15}/></span>
              <span className="vz-btn-testo">{t('btn_upload')}</span>
            </button>
          </>}
          {/* Il campo si SVUOTA dopo ogni scelta. Senza, riscegliere lo stesso
              file non emette nessun evento — il browser vede lo stesso valore
              e sta zitto — e il tasto sembra morto. Con la coda diventa un
              guaio vero: annulli un caricamento, riprovi con lo stesso file e
              non succede niente. I file si prendono PRIMA di svuotare, perche'
              svuotare il campo azzera anche la sua lista. */}
          <input ref={fileInputRef} type="file" multiple style={{display:'none'}}
                 onChange={e=>{ const scelti = Array.from(e.target.files); e.target.value=''; handleFiles(scelti); }}/>
          <input ref={folderInputRef} type="file" style={{display:'none'}} webkitdirectory="true" directory="true" multiple onChange={handleFolderUpload}/>
          <input ref={replaceInputRef} type="file" style={{display:'none'}} onChange={handleReplaceFile}/>
        </div>

        <div className="vz-header-right">
          {/* 24 accounts (found 2026-08-28) have their username field set to
              the full email instead of the short handle generateUsername.js
              is supposed to guarantee (username.includes('@') never happens
              by design) — display-only normalization here so the header
              never shows "@name@domain.com", without touching the stored
              value itself (WebDAV auth and the admin panel key off the real
              one, unaffected by this). */}
          {/* OPZIONI IN ALTO A DESTRA (2026-09-05): lo username era uno <span>
              morto, e le impostazioni si raggiungevano SOLO dalla barra in
              fondo (persona -> menu -> Profilo), cioe' due passi in un punto
              dove nessuno cerca le impostazioni dell'account. L'angolo in alto
              a destra e' la convenzione universale per "il mio account".
              Apre lo STESSO menu della dock (toggleFlyout('profile')): niente
              menu nuovo, le voci restano allineate per costruzione. La dock
              resta dov'e': si aggiunge una via, non se ne sposta una. Su
              telefono lo username spariva (App.css): al suo posto l'icona. */}
          <button
            type="button"
            ref={bottoneAccountRef}
            className={`vz-account-btn${profileMenuOpen ? ' aperto' : ''}`}
            onClick={()=>{ setMenuDaAccount(true); toggleFlyout('profile'); }}
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
            title={t('dockmenu_profile')}
          >
            {/* LA ROTELLA (2026-09-19). La figurina della persona diceva
                "questo sei tu"; qui dentro invece ci sono le opzioni, e il
                segno universale delle opzioni e' la rotella. Una sola, in
                questo pulsante: la rotella separata dell'amministrazione e'
                stata tolta dal desktop, perche' portava dove porta gia' una
                voce di questo menu. */}
            <span className="vz-account-icona" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="17" height="17" focusable="false">
                <path d="M10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M16.2 12.1a1.3 1.3 0 0 0 .26 1.43l.05.05a1.55 1.55 0 1 1-2.2 2.2l-.04-.05a1.3 1.3 0 0 0-1.44-.26 1.3 1.3 0 0 0-.79 1.19v.13a1.55 1.55 0 1 1-3.1 0v-.07a1.3 1.3 0 0 0-.85-1.19 1.3 1.3 0 0 0-1.43.26l-.05.05a1.55 1.55 0 1 1-2.2-2.2l.05-.05a1.3 1.3 0 0 0 .26-1.43 1.3 1.3 0 0 0-1.19-.79H3.4a1.55 1.55 0 1 1 0-3.1h.07a1.3 1.3 0 0 0 1.19-.85 1.3 1.3 0 0 0-.26-1.43l-.05-.05a1.55 1.55 0 1 1 2.2-2.2l.05.05a1.3 1.3 0 0 0 1.43.26h.06a1.3 1.3 0 0 0 .79-1.19V3.4a1.55 1.55 0 1 1 3.1 0v.07a1.3 1.3 0 0 0 .79 1.19 1.3 1.3 0 0 0 1.43-.26l.05-.05a1.55 1.55 0 1 1 2.2 2.2l-.05.05a1.3 1.3 0 0 0-.26 1.43v.06a1.3 1.3 0 0 0 1.19.79h.13a1.55 1.55 0 1 1 0 3.1h-.07a1.3 1.3 0 0 0-1.19.79Z"
                      fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="vz-username">@{(user?.username || '').split('@')[0]}</span>
            {/* LA FRECCETTA CHE DICE CHE SI APRE (2026-09-19). Senza la
                pillola in fondo, questo e' l'unico posto dove stanno le
                opzioni sul desktop, e prima non sembrava un comando: nome
                utente e basta, come un'etichetta. La freccia e' l'unico
                segno universale di "qui sotto c'e' dell'altro", e ruota
                quando il menu e' aperto. Decorativa: il nome del comando
                per chi legge con la voce resta il titolo del pulsante. */}
            <span className="vz-account-freccia" aria-hidden="true">
              <svg viewBox="0 0 12 12" width="11" height="11" focusable="false">
                <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor"
                      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>
          {isAdmin && <button className="vz-icon-btn" onClick={onAdmin} title={t('admin')}>⚙️</button>}
        </div>
      </header>

      {/* Storage/plan indicator — fixed pill near the Dock on desktop
          (unchanged). On mobile it's sticky right below the header instead
          (see .dock-storage-wrap mobile override in App.css) — it MUST be a
          sibling of .vz-header here, outside .vz-body: .vz-body has
          overflow:hidden, which makes it (not the viewport) the reference
          box for any position:sticky descendant, even though .vz-body itself
          never actually scrolls (the page does) — a sticky child inside it
          just sits at a fixed offset from .vz-body's own top edge, which is
          already below the header, so it appeared stranded mid-screen
          instead of stuck under the header. .vz-header sits out here for the
          same reason and its sticky positioning works correctly. */}
      <div className="dock-storage-wrap">
        {/* Status only — no button, no CTA text inside. Used to merge the 2TB
            promo into this same row on mobile (dock-storage-cta), which is
            exactly what caused it: a realistic free-space figure like
            "1023.9 MB" plus "Passa a 2TB — €79 ›" don't both fit on a real
            360px phone, and whichever lost the ellipsis race made neither
            readable. The promo now lives in its own row instead — see
            .vz-mobile-upgrade-banner, above the mobile upload bar — so this
            bar only ever has to fit the status it's actually meant to show. */}
        {/* LA PASTIGLIA PORTA AI PIANI (2026-09-19). Era uno stato muto: chi
            legge "48/50 file" sta guardando proprio il motivo per cui
            vorrebbe piu' spazio, e non aveva niente da toccare. Stessa meta'
            del comando nella barra. Resta un riquadro di stato, non diventa
            un bottone: si aggiunge il ruolo e la tastiera, il testo dentro
            non cambia di una lettera. */}
        <div
          className={`dock-storage dock-storage-cliccabile${livelloMuro >= 95 ? ' dock-storage-rossa' : livelloMuro >= 80 ? ' dock-storage-ambra' : ''}`}
          style={{'--pc':planColor}}
          role="button"
          tabIndex={0}
          title={t('spazio_vedi_piani')}
          onClick={onUpgrade}
          onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onUpgrade(); } }}
        >
          <span className="dock-storage-left">
            {/* Su schermo stretto sparisce il nome del piano: a 360 px la
                pastiglia deve dire due numeri, non un'etichetta piu' due
                numeri tagliati a meta'. */}
            {!strettissimo && <span className="dock-storage-plan">{planEmoji ? planEmoji + ' ' : ''}{planLabel}</span>}
            {/* DUE FORME, non una accorciata coi puntini (2026-09-13).
                A 360 px la forma lunga veniva tagliata a meta' — "file
                esauriti: 5…" — e un numero tagliato e' peggio di nessun
                numero. Sotto i 480 px si dicono i due numeri e basta:
                "1,0 GB liberi · 50/50 file". In rosso quello dei due che e'
                al muro, cosi' si vede quale dei due ti ferma.
                Il messaggio lungo non si ripete: se il banner rosso e' in
                alto, lo dice gia' lui. */}
            <span className="dock-storage-nums">
              {strettissimo ? (
                <>
                  <strong style={{color: tintaMuro(livSpazio) || planColorText}}>{pesoCorto(freeSpace)}</strong>
                  {' '}{t('dock_free_label')}
                  {quota?.maxFiles > 0 && (
                    <span style={{color: tintaMuro(livFile) || undefined}}>
                      {' · '}{quota.fileCount}/{quota.maxFiles}{' '}{t('dock_file_corto')}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <strong style={{color: tintaMuro(livSpazio) || planColorText}}>{formatBytes(freeSpace)}</strong> {t('dock_free_label')}
                  <span className="dock-storage-dim"> · {t('dock_used_of_total', {used: formatBytes(quota?.used||0), total: formatBytes(quota?.total||0)})}</span>
                  {quota?.maxFiles > 0 && (
                    <span className="dock-storage-file" style={{color: tintaMuro(livFile) || undefined}}>
                      {' · '}
                      {fileAlMuro
                        ? t('dock_file_pieni', { massimo: quota.maxFiles })
                        : t('dock_file_su', { usati: quota.fileCount, massimo: quota.maxFiles })}
                    </span>
                  )}
                </>
              )}
            </span>
          </span>
          <div className="dock-storage-track">
            {/* La barretta segue il piu' grave dei due muri, non il solo spazio. */}
            <div className="dock-storage-fill" style={{width:`${Math.max(quotaPct, Math.min(100, pcFileMuro))}%`, background: tintaMuro(livelloMuro) || planColor}}/>
          </div>
        </div>
      </div>

      {/* 2026-09-04 — la scheda dell'offerta e' stata eliminata da qui.
          Nel vault l'offerta vive in un posto solo: .vz-upgrade-strip,
          ancorata in basso sopra la dock (e .vz-header-upgrade-center su
          desktop). Una sola, mai due. */}

      {avvisoSblocco && (
        <div className="vz-sblocco-avviso" role="status">
          <span aria-hidden="true">🔓</span>
          <span>{t('sblocco_dal_dispositivo')}</span>
          <button type="button" className="vz-sblocco-x" onClick={() => setAvvisoSblocco(false)}
                  aria-label={t('sblocco_chiudi')}>✕</button>
        </div>
      )}

      <div className="vz-body">
        {/* ── SIDEBAR ──────────────────────────────────── */}
        {sidebarOpen && <div className="vz-sidebar-backdrop" onClick={()=>setSidebarOpen(false)}/>}
        {(
          <>
            <aside className={`vz-sidebar${colonnaStretta ? ' vz-sidebar-stretta' : ''}${sidebarOpen ? ' vz-sidebar-aperta' : ''}`}>
              {/* LA FRECCETTA (2026-09-19). Sempre visibile, non solo al
                  passaggio del mouse: un comando che compare solo quando ci
                  passi sopra non lo trova chi non sa che esiste. Resta in
                  cima anche a colonna stretta, cosi' da li' si riapre. Le tre
                  righe accanto al logo continuano a fare la stessa cosa. */}
              <button type="button" className="vz-colonna-freccia" onClick={cambiaColonna}
                      title={t(colonnaStretta ? 'colonna_apri' : 'colonna_chiudi')}
                      aria-label={t(colonnaStretta ? 'colonna_apri' : 'colonna_chiudi')}
                      aria-expanded={!colonnaStretta}>
                {colonnaStretta ? '»' : '«'}
              </button>
              <div className="vz-sidebar-section">
                <button className={`vz-sidebar-item ${!isRecentView && !currentFolder?'active':''}`} onClick={goHomeTab}
                        title={t('sidebar_my_vault')}>
                  <span className="vz-sidebar-icona" aria-hidden="true">🗄</span>
                  <span className="vz-sidebar-testo">{t('sidebar_my_vault')}</span>
                </button>
                <button className={`vz-sidebar-item ${isRecentView?'active':''}`} onClick={goRecentTab}
                        title={t('sidebar_recent')}>
                  <span className="vz-sidebar-icona" aria-hidden="true">🕐</span>
                  <span className="vz-sidebar-testo">{t('sidebar_recent')}</span>
                </button>
                <button className="vz-sidebar-item" onClick={()=>setSharedModalOpen(true)}
                        title={t('sidebar_shared')}>
                  <span className="vz-sidebar-icona" aria-hidden="true">🔗</span>
                  <span className="vz-sidebar-testo">{t('sidebar_shared')}</span>
                </button>
                {/* Sempre visibile, anche senza nemmeno un preferito: una voce
                    che compare solo quando hai gia' usato la funzione non la
                    fa scoprire a nessuno. Chi non ne ha trova la schermata
                    che glielo dice. */}
                <button className={`vz-sidebar-item ${isPreferitiView?'active':''}`} onClick={goPreferitiTab}
                        title={t('sidebar_preferiti')}>
                  <span className="vz-sidebar-icona" aria-hidden="true">★</span>
                  <span className="vz-sidebar-testo">{t('sidebar_preferiti')}</span>
                </button>
              </div>
              <div className="vz-sidebar-section">
                <p className="vz-sidebar-label">{t('sidebar_folders_label')}</p>
                <FolderTree allFolders={allFolders} currentFolder={currentFolder}
                  onNavigate={f=>{ setMobileTab('home'); openFolder(f); closeSidebarOnMobile(); }}/>
                <button className="vz-sidebar-new-folder" onClick={()=>{setNewFolderName('');setFolderError('');setShowNewFolder(true);}}>
                  {t('sidebar_new_folder')}
                </button>
              </div>
              <div className="vz-sidebar-bottom">
                <div className="vz-sidebar-quota">
                  <div className="vz-sq-bar"><div style={{width:`${quotaPct}%`,height:'100%',background:planColor,borderRadius:99}}/></div>
                  <span className="vz-sq-text">{formatBytes(quota?.used||0)} / {formatBytes(quota?.total||0)}</span>
                </div>
              </div>
            </aside>
          </>
        )}

        {/* ── MAIN ─────────────────────────────────────── */}
        <div className="vz-main-wrap">
          {/* L'AVVISO DELLO STATO STA QUI (2026-09-13). Da desktop e' dentro
              la colonna, largo quanto l'elenco e subito sopra: cosi' l'elenco
              comincia sotto di lui e non sotto la somma di tre barre fisse.
              Sotto i 640px il componente si mette da solo in cima fissa —
              stesso pezzo, due collocazioni, vedi BarraSpazio.css. */}
          <BarraSpazio quota={quota} onUpgrade={onUpgrade} />

          {/* Content */}
          <main ref={mainRef}
            className={`vz-main ${dragOver?'vz-drag-over':''}`}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={e=>{if(!e.currentTarget.contains(e.relatedTarget))setDragOver(false);}}
            onDrop={e=>{
              e.preventDefault(); setDragOver(false);
              // webkitGetAsEntry() va chiamato QUI, sincrono: DataTransferItemList
              // viene svuotata appena il gestore ritorna, e da un await in poi
              // e.dataTransfer.items e' gia' vuoto. Per questo si raccolgono
              // prima le voci e solo dopo si fa il lavoro asincrono.
              const voci = Array.from(e.dataTransfer.items || [])
                .map(i => (i.webkitGetAsEntry ? i.webkitGetAsEntry() : null))
                .filter(Boolean);
              if (voci.some(v => v.isDirectory)) return handleDropCartella(voci);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={()=>{setSelected(new Set());setCtxMenu(null);}}
            onContextMenu={ctxOnBackground}
          >
            {dragOver && (
              <div className="vz-drop-target">
                <div className="vz-drop-box">
                  <span className="vz-drop-icon">☁️</span>
                  <p>{t('drop_to_upload')}</p>
                  <small>{t('drop_encrypted_note')}</small>
                </div>
              </div>
            )}

            {/* Mobile-only back-nav (App.css hides on desktop, where
                .vz-header-crumb does this job instead). 2026-08-28: folders
                used to render in a separate boxed 2-col grid here, on top
                of ALSO rendering (hidden) as .vz-row-folder below — now
                they render just once, as ordinary rows at the top of the
                same list (App.css no longer hides .vz-row-folder on
                mobile). This element is now only the "go back up" control,
                so it only needs to exist when there's somewhere to go back
                to — nothing renders at the true root. */}
            {!vistaSpeciale && !viewLoading && folderPath.length>0 && (
              <div className="vz-mobile-backnav">
                {/* Full breadcrumb would run off-screen past 2-3 levels deep
                    — mobile keeps just "‹ parent" (goes up one level) plus
                    the current folder's name. */}
                <button className="vz-mobile-backnav-btn" aria-label={t('nav_back')} onClick={()=>{
                  if (folderPath.length===1) navigateTo(null,-1);
                  else navigateTo(folderPath[folderPath.length-2].id, folderPath.length-2);
                }}>
                  ‹ {folderPath.length===1 ? t('vault_home') : folderPath[folderPath.length-2].name}
                </button>
                <span className="vz-mobile-backnav-current">{folderPath[folderPath.length-1].name}</span>
              </div>
            )}

            {!vaultDelTuttoVuoto && <div className="vz-status">
              {viewLoading ? <><span className="vz-spinner" style={{borderTopColor:planColor}}/> {t('loading')}</> :
                <span>{t('items_count', {count: totalItems})}{hasSelection?t('selected_suffix', {count: selected.size}):''}</span>}
              {/* Mobile: "Double-tap to open" only while the list is empty
                  (nothing else useful to show, and nothing to learn yet) —
                  shown on every row otherwise, it becomes permanent noise
                  the moment there's anything to actually double-tap.
                  Desktop keeps its hint unconditionally, unchanged. */}
              {(window.innerWidth > 700 || totalItems === 0) && (
                <span className="vz-status-hint">{window.innerWidth <= 700 ? t('status_hint_mobile') : t('status_hint')}</span>
              )}
            </div>}

            {/* L'ORDINE (2026-09-10): PRIMA cosa fare, POI l'offerta.
                Fino a stasera il blocco dell'offerta stava sopra, e con il
                riquadro "perche' i posti" dentro occupava tutto lo schermo di
                un telefono: chi apriva il vault la prima volta doveva
                scorrere per trovare "Carica il primo file", cioe' l'unica
                cosa che deve fare. Adesso il pulsante e' la prima cosa sotto
                la fascia; l'offerta e la spiegazione dei posti restano, sotto
                e piu' piccole. Nessun testo e' cambiato. */}
            {vaultDelTuttoVuoto && (
              <div className="vz-primo-accesso">
                <h2 className="vz-primo-titolo">{t('vuoto_titolo')}</h2>
                <p className="vz-primo-riga">{t('vuoto_riga')}</p>
                <button className="vz-primo-cta" onClick={()=>fileInputRef.current.click()}>
                  {t('vuoto_cta')}
                </button>
                <p className="vz-primo-trascina">{t('vuoto_trascina')}</p>
              </div>
            )}

            {/* Vault del tutto vuoto: UN SOLO blocco centrato, niente altro
                (2026-09-05). Il vecchio stato vuoto generico resta per le
                cartelle vuote e per "Recenti". */}
            {/* L'OFFERTA RESTA, ma SOTTO lo stato vuoto (2026-09-10).
                Il 7 settembre era stata messa sopra, annullando lo spostamento
                di ddef4c5 che l'aveva tolta del tutto: chi entra la prima
                volta e' free per definizione, ed era l'unico che non vedeva
                alcun prezzo. Quel motivo vale ancora — l'offerta si vede — ma
                stare SOPRA le costava il pulsante "Carica il primo file", che
                su un telefono finiva fuori schermo. Resta IN FLUSSO, non
                fissata in fondo. La striscia fissa resta per il vault con dei
                file. */}
            {/* Stessa ragione della striscia fissa: dentro l'app non ci sono
                percorsi d'acquisto. */}
            {/* VAULT VUOTO ALLINEATO AL VAULT CON I FILE (2026-09-16 sera).
                Via il riquadro bianco con il titolo «Prima di cominciare…» e i
                due bottoni 1 TB / 2 TB: nel vault l'offerta e' UNA, la striscia
                (BannerOfferta posto="fondo"), identica a quella del vault con
                i file — stessi testi, conto alla rovescia e posti veri. Qui
                sta in flusso, SOTTO «Carica il primo file»: prima l'azione, poi
                l'offerta. La condizione e' showUpgradeStrip (senza il guardiano
                della finestra di benvenuto) dentro !dentroApp(), come ovunque.
                La scheda «Prova» resta, da sola, sotto la striscia. */}
            {vaultDelTuttoVuoto && (
              <div className="vz-primo-sotto">
                {!dentroApp() && showUpgradeStrip && (
                  <BannerOfferta posto="fondo" soloSeGratuito pianoUtente={planId} />
                )}
                {/* Le stringhe sono quelle della pagina dei piani (namespace
                    plans), non copie. feature_2 e' "Max {{limite}} per file":
                    qui non c'e' il limite dal server, quindi si toglie. */}
                <div className="vz-primo-free">
                  <span className="vz-primo-free-nome">{t('plans:plan_free_name')}</span>
                  <span className="vz-primo-free-ora">{t('plans:plan_free_desc')}</span>
                  <ul className="vz-primo-free-voci">
                    <li>{t('plans:plan_free_feature_1')}</li>
                    <li>{t('plans:plan_free_feature_3')}</li>
                  </ul>
                </div>
              </div>
            )}


            {!vaultDelTuttoVuoto && !viewLoading && totalItems===0 && (
              <div className="vz-empty">
                <svg className="vz-empty-icon" width="64" height="64" viewBox="0 0 64 64" fill="none">
                  <path d="M8 22C8 19.8 9.8 18 12 18h14l4 4h24c2.2 0 4 1.8 4 4v26c0 2.2-1.8 4-4 4H12c-2.2 0-4-1.8-4-4V22z" fill="#1e293b" stroke="#334155" strokeWidth="1.5"/>
                </svg>
                <p className="vz-empty-title">{isPreferitiView ? t('empty_no_preferiti_title') : isRecentView ? t('empty_no_recent_title') : t('empty_folder_title')}</p>
                <p className="vz-empty-sub">{isPreferitiView ? t('empty_preferiti_sub') : isRecentView ? t('empty_recent_sub') : t('empty_folder_sub')}</p>
                {!vistaSpeciale && <button className="vz-btn vz-btn-primary" onClick={()=>fileInputRef.current.click()}>⬆ {t('empty_upload_first')}</button>}
              </div>
            )}

            {/* LIST */}
            {!vaultDelTuttoVuoto && !viewLoading && viewMode==='list' && totalItems>0 && (
              <div className="vz-table">
                <BarraOrdinamentoMobile />
                {/* L'interruttore compare SOLO se ci sono davvero file
                    nascosti: una casella che non nasconde niente sarebbe
                    solo un controllo in piu' da leggere. */}
                {(nascostiN > 0 || mostraNascosti) && (
                  <label className="vz-nascosti-riga">
                    <input
                      type="checkbox"
                      className="vz-checkbox"
                      checked={mostraNascosti}
                      onChange={(e) => { setMostraNascosti(e.target.checked); safeSetItem('lc_mostra_nascosti', e.target.checked ? '1' : '0'); }}
                    />
                    <span>{t('mostra_nascosti')}</span>
                    {!mostraNascosti && nascostiN > 0 && (
                      <span className="vz-nascosti-conta">{t('file_nascosti_n', { count: nascostiN })}</span>
                    )}
                  </label>
                )}
                {/* Le intestazioni ERANO gia' cliccabili, ma non lo
                    sembravano: nessuno sfondo al passaggio, e la freccia
                    colorata var(--border), cioe' il colore dei bordi. Un
                    cliente non l'ha trovata, e guardandola non l'ha
                    riconosciuta nemmeno chi l'ha fatta. Ora sono pulsanti
                    veri — raggiungibili anche da tastiera e annunciati agli
                    screen reader con aria-sort. */}
                <div className="vz-table-head" role="row">
                  <div className="vz-col-check"><input type="checkbox" className="vz-checkbox" checked={allSelected} onChange={selectAll}/></div>
                  <TestaOrdinabile col="name"  classe="vz-col-name"><span className="vz-col-label">{t('col_name')}</span></TestaOrdinabile>
                  <TestaOrdinabile col="date"  classe="vz-col-date">{t('col_date')}</TestaOrdinabile>
                  <TestaOrdinabile col="size"  classe="vz-col-size">{t('col_size')}</TestaOrdinabile>
                  <div className="vz-col-actions"><span className="vz-col-label">{t('col_actions')}</span></div>
                </div>
                {viewFolders.map((folder,i)=>(
                  <div key={folder.id} className={`vz-row vz-row-folder ${i%2===1?'vz-row-even':''}`}
                    onDoubleClick={()=>openFolder(folder)}
                    onContextMenu={e=>ctxOnFolder(e,folder)}
                    onClick={e=>e.stopPropagation()}>
                    <div className="vz-col-check"/>
                    <div className="vz-col-name">
                      <FolderAvatar size={32} name={folder.name}/>
                      <div className="vz-file-info">
                        {renamingFolder===folder.id
                          ? <input autoFocus className="vz-rename-input" value={renameValue}
                              onChange={e=>setRenameValue(e.target.value)}
                              onKeyDown={e=>{if(e.key==='Enter')handleRename(folder);if(e.key==='Escape')setRenamingFolder(null);}}
                              onBlur={()=>setRenamingFolder(null)} onClick={e=>e.stopPropagation()}/>
                          : <span className="vz-filename">{folder.name}</span>}
                        <span className="vz-filemeta">{t('type_folder')}</span>
                      </div>
                    </div>
                    <div className="vz-col-date">{formatDate(folder.createdAt, i18n.language)}</div>
                    <div className="vz-col-size">—</div>
                    <div className="vz-col-actions">
                      <button className="vz-action-btn" title={t('action_open')} onClick={()=>openFolder(folder)}>→</button>
                      <button className="vz-action-btn" title={t('action_rename')} onClick={e=>{e.stopPropagation();setRenamingFolder(folder.id);setRenameValue(folder.name);}}>✏</button>
                      <button className="vz-action-btn vz-action-del" title={t('action_delete')} onClick={e=>{e.stopPropagation();if(window.confirm(t('confirm_delete_file', {name: folder.name})))deleteFolder(folder.id);}}>🗑</button>
                      {/* Touch-friendly stand-in for right-click (disabled on mobile,
                          see ctxOnFolder/ctxOnFile) — same menu, same handler, just
                          triggered by a tap instead of a context-menu event. */}
                      <button className="vz-action-btn vz-action-more" title={t('action_more')} onClick={e=>ctxOnFolder(e,folder)}>⋯</button>
                    </div>
                  </div>
                ))}
                {sortedFiles.map((file,fi)=>{
                  const [nameHead, nameTail] = splitNameForTruncation(file.filename);
                  // Continuous index across folders+files, not a fresh
                  // per-map counter — the stripe has to keep alternating
                  // across the folder/file boundary, not reset at it.
                  const rowIndex = viewFolders.length + fi;
                  return (
                  <div key={file.id}
                    className={`vz-row ${rowIndex%2===1?'vz-row-even':''} ${selected.has(file.id)?'vz-row-selected':''}`}
                    onDoubleClick={()=>handlePreview(file)}
                    onContextMenu={e=>ctxOnFile(e,file)}
                    onClick={e=>e.stopPropagation()}>
                    <div className="vz-col-check" onClick={e=>toggleSelect(file.id,e)}>
                      <input type="checkbox" className="vz-checkbox" checked={selected.has(file.id)} onChange={e=>toggleSelect(file.id,e)} onClick={e=>e.stopPropagation()}/>
                    </div>
                    <div className="vz-col-name">
                      {/* Shrunk on mobile — the DIMENSIONE column is gone there (see
                          .vz-col-size below) and the extension is already visible in
                          the name itself (see splitNameForTruncation), so this ~50px
                          colored type badge was mostly repeating information the name
                          already gives; the name gets that width back instead. */}
                      {/* Miniatura solo per le immagini idonee: tutto il resto
                          tiene l'icona di sempre, identica a prima. */}
                      {puoAvereMiniatura(file)
                        ? <ImageThumb file={file} encKey={encKey} size={window.innerWidth<=700?20:32}
                                      onOpen={()=>handlePreview(file)}
                                      fallback={<FileAvatar filename={file.filename} size={window.innerWidth<=700?20:32}/>}/>
                        : <FileAvatar filename={file.filename} size={window.innerWidth<=700?20:32}/>}
                      <div className="vz-file-info">
                        <span className="vz-filename" title={file.filename}>
                          <span className="vz-filename-head">{nameHead}</span>
                          {nameTail && <span className="vz-filename-tail">{nameTail}</span>}
                        </span>
                        {/* Desktop keeps the "Encrypted"/"Not encrypted" sentence (App.css
                            hides this on mobile). Mobile shows the lock icon plus the file
                            size — size used to ALSO have its own column (.vz-col-size) on
                            mobile, shown here AND there at once (duplication caught after
                            that first mobile pass shipped); now the column is mobile-hidden
                            and this is the only place size appears there. */}
                        <span className="vz-filemeta vz-filemeta-desktop">
                          {file.encrypted?t('file_encrypted'):t('file_not_encrypted')}
                          {/* Un file etichettato "non crittografato" dentro un
                              prodotto che vende cifratura e' una domanda che
                              merita risposta li' dove nasce, non nella guida.
                              Si apre al clic: su telefono un title non si vede. */}
                          {!file.encrypted && (
                            <button
                              type="button"
                              className="vz-aiuto-cifratura"
                              title={t('non_cifrato_spiega')}
                              aria-label={t('non_cifrato_aiuto')}
                              onClick={(e)=>{ e.stopPropagation(); setSpiegaCifratura(v=>v===file.id?null:file.id); }}
                            >?</button>
                          )}
                          {spiegaCifratura===file.id && (
                            <span className="vz-aiuto-bolla" role="tooltip">
                              {t('non_cifrato_spiega').split('\n').map((r,i)=><span key={i}>{r}</span>)}
                            </span>
                          )}
                        </span>
                        <span className="vz-filemeta vz-filemeta-mobile">{file.encrypted?'🔒':'🔓'} · {formatBytes(file.size)}</span>
                      </div>
                    </div>
                    <div className="vz-col-date">{formatDate(file.uploadedAt, i18n.language)}</div>
                    <div className="vz-col-size">{formatBytes(file.size)}</div>
                    <div className="vz-col-actions">
                      {getPreviewType(file.filename) && (
                        <button className="vz-action-btn vz-action-prev" title={t('action_preview')} onClick={e=>{e.stopPropagation();handlePreview(file);}} disabled={actionId===file.id+'p'}>
                          {actionId===file.id+'p'?'⏳':'👁'}
                        </button>
                      )}
                      <button className="vz-action-btn vz-action-dl" title={t('action_download')} onClick={e=>{e.stopPropagation();handleDownloadSingle(file);}} disabled={actionId===file.id+'d'}>
                        {actionId===file.id+'d'?'⏳':'⬇'}
                      </button>
                      <button className="vz-action-btn vz-action-del" title={t('action_delete')} onClick={e=>{e.stopPropagation();handleDeleteSingle(file);}} disabled={actionId===file.id+'x'}>
                        {actionId===file.id+'x'?'⏳':'🗑'}
                      </button>
                      <button className="vz-action-btn vz-action-more" title={t('action_more')} onClick={e=>ctxOnFile(e,file)}>⋯</button>
                    </div>
                  </div>
                  );})}
              </div>
            )}

            {/* GRID */}
            {!vaultDelTuttoVuoto && !viewLoading && viewMode==='grid' && totalItems>0 && (
              <div className="vz-grid">
                {viewFolders.map(folder=>(
                  <div key={folder.id} className="vz-card vz-card-folder"
                    onDoubleClick={()=>openFolder(folder)}
                    onContextMenu={e=>ctxOnFolder(e,folder)}
                    onClick={e=>e.stopPropagation()}>
                    <FolderAvatar size={48} name={folder.name}/>
                    {renamingFolder===folder.id
                      ? <input autoFocus className="vz-rename-input" value={renameValue}
                          onChange={e=>setRenameValue(e.target.value)}
                          onKeyDown={e=>{if(e.key==='Enter')handleRename(folder);if(e.key==='Escape')setRenamingFolder(null);}}
                          onBlur={()=>setRenamingFolder(null)} onClick={e=>e.stopPropagation()}/>
                      : <span className="vz-card-name">{folder.name}</span>}
                    <div className="vz-card-actions">
                      <button onClick={()=>openFolder(folder)}>→</button>
                      <button onClick={e=>{e.stopPropagation();setRenamingFolder(folder.id);setRenameValue(folder.name);}}>✏</button>
                      <button onClick={e=>{e.stopPropagation();if(window.confirm(t('confirm_delete_file', {name: folder.name})))deleteFolder(folder.id);}}>🗑</button>
                      <button className="vz-action-more" title={t('action_more')} onClick={e=>ctxOnFolder(e,folder)}>⋯</button>
                    </div>
                  </div>
                ))}
                {sortedFiles.map(file=>(
                  <div key={file.id}
                    className={`vz-card ${selected.has(file.id)?'vz-card-selected':''}`}
                    onDoubleClick={()=>handlePreview(file)}
                    onContextMenu={e=>ctxOnFile(e,file)}
                    onClick={e=>e.stopPropagation()}>
                    <div className="vz-card-check" onClick={e=>toggleSelect(file.id,e)}>
                      <input type="checkbox" className="vz-checkbox" checked={selected.has(file.id)} onChange={e=>toggleSelect(file.id,e)} onClick={e=>e.stopPropagation()}/>
                    </div>
                    {/* LA PASTIGLIA DEL LUCCHETTO (2026-09-16). Nell'elenco lo stato di
                        cifratura si legge sotto il nome; in griglia mancava. E non basta
                        un lucchetto sui cifrati e niente sugli altri: l'assenza di un
                        segno non e' un segno — l'utente non distingue "non cifrato" da
                        "l'icona non e' ancora comparsa". Quindi OGNI scheda file ha il
                        suo marchio: chiuso verde = cifrato sul dispositivo; aperto ambra
                        = encrypted=0 (WebDAV), con il tooltip che lo dice. Le cartelle
                        no: non hanno uno stato di cifratura. */}
                    <span className={`vz-cifra ${file.encrypted ? 'vz-cifra-si' : 'vz-cifra-no'}`}
                          title={file.encrypted ? t('cifra_tooltip_si') : t('cifra_tooltip_no')}
                          aria-label={file.encrypted ? t('cifra_tooltip_si') : t('cifra_tooltip_no')} role="img">
                      {file.encrypted
                        ? <svg viewBox="0 0 14 14" focusable="false" aria-hidden="true"><rect x="2.6" y="6.2" width="8.8" height="6.6" rx="1.4" fill="currentColor"/><path d="M4.6 6.2V4.3a2.4 2.4 0 0 1 4.8 0v1.9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        : <svg viewBox="0 0 14 14" focusable="false" aria-hidden="true"><rect x="2.6" y="6.2" width="8.8" height="6.6" rx="1.4" fill="currentColor"/><path d="M4.6 6.2V4.3a2.4 2.4 0 0 1 4.8 0" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>}
                    </span>
                    {/* La stellina sta in alto a destra, alla sinistra del
                        lucchetto: i due angoli erano gia' occupati (selezione a
                        sinistra, cifratura a destra) e nessuno dei due si
                        sposta. Vuota se non e' preferito, piena se lo e'. */}
                    <button className={`vz-stella ${file.preferitoAt ? 'vz-stella-si' : ''}`}
                            onClick={e=>{e.stopPropagation();cambiaPreferito(file);}}
                            disabled={preferitoBusy===file.id}
                            title={t(file.preferitoAt ? 'menu_preferito_togli' : 'menu_preferito_metti')}
                            aria-label={t(file.preferitoAt ? 'menu_preferito_togli' : 'menu_preferito_metti')}
                            aria-pressed={!!file.preferitoAt}>
                      {file.preferitoAt ? '★' : '☆'}
                    </button>
                    {/* Vista a griglia: stessa regola della lista, miniatura per le
                        immagini idonee e icona per tutto il resto. Qui il riquadro e'
                        piu' grande perche' la scheda ha spazio, ed e' il posto dove
                        l'anteprima serve di piu': la griglia esiste apposta per
                        guardare le foto. */}
                    {puoAvereMiniatura(file)
                      ? <ImageThumb file={file} encKey={encKey} size={72}
                                    onOpen={()=>handlePreview(file)}
                                    fallback={<FileAvatar filename={file.filename} size={48}/>}/>
                      : <FileAvatar filename={file.filename} size={48}/>}
                    <span className="vz-card-name" title={file.filename}>{file.filename}</span>
                    {/* Solo se c'e': senza didascalia la scheda resta identica a prima. */}
                    {file.didascaliaTesto
                      && <span className="vz-card-didascalia" title={file.didascaliaTesto}>{file.didascaliaTesto}</span>}
                    <span className="vz-card-size">{formatBytes(file.size)}</span>
                    <div className="vz-card-actions">
                      {getPreviewType(file.filename) && <button onClick={e=>{e.stopPropagation();handlePreview(file);}} disabled={actionId===file.id+'p'} title={t('action_preview')}>{actionId===file.id+'p'?'⏳':'👁'}</button>}
                      <button onClick={e=>{e.stopPropagation();handleDownloadSingle(file);}} disabled={actionId===file.id+'d'}>{actionId===file.id+'d'?'⏳':'⬇'}</button>
                      <button onClick={e=>{e.stopPropagation();handleDeleteSingle(file);}} disabled={actionId===file.id+'x'}>{actionId===file.id+'x'?'⏳':'🗑'}</button>
                      <button className="vz-action-more" title={t('action_more')} onClick={e=>ctxOnFile(e,file)}>⋯</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </main>

          <div className="vz-statusbar">
            <span>{t('items_count', {count: totalItems})}{hasSelection?t('selected_suffix', {count: selected.size}):''}</span>
            <span>🔒 AES-256-GCM · lifetimecloud.me</span>
          </div>
        </div>
      </div>

      {/* Striscia dell'offerta — ANCORATA (position:fixed, vedi App.css): in
          flusso dopo l'elenco finiva sepolta sotto i file man mano che se ne
          caricavano. Sta col gruppo fisso in fondo: desktop sopra la pillola
          "Free · 1 GB liberi", mobile sopra la barra "Carica file". Stesso
          punto dello schermo con zero file o duecento. Free soltanto
          (showUpgradeStrip): chi ha acquistato non la vede mai. Tutta
          cliccabile, stesso flusso di checkout degli altri punti d'acquisto. */}
      {/* DENTRO L'APP QUESTA STRISCIA NON C'E' (2026-09-13).
          E' un percorso d'acquisto: due pulsanti che portano dritti a Stripe.
          Dentro un'app le regole di Google non ammettono un percorso che porti
          a pagare fuori dal loro sistema, e non e' negoziabile. Sul sito —
          anche dal telefono, nel browser — resta identica: li' quella regola
          non esiste. */}
      {/* I due riquadri 1TB/2TB (vz-upgrade-strip-coppia, con le pastiglie
          posti) sono stati TOLTI (2026-09-16): un solo invito, la striscia
          offerta agganciata in fondo (sotto). */}


      {/* LA STRISCIA OFFERTA, AGGANCIATA IN FONDO (2026-09-16). Fissa appena
          sopra "Carica file" (mobile) / sopra la dock (desktop), verde piena.
          Dentro l'app NON si mostra (upsell): gate !dentroApp(). Solo free con
          prezzo pronto e fuori dalla finestra di benvenuto (showUpgradePill).
          Pubblica la propria altezza (--vz-offerta-fondo-h) e la lista le
          somma un padding-bottom cosi' l'ultimo file non finisce coperto. */}

      {/* IL FONDO FISSO DEL FILE MANAGER (2026-09-16): un solo contenitore
          flex sopra la dock, cosi' i pezzi NON si sovrappongono mai. Dall'alto
          in basso: "Carica file" (mobile) poi la striscia offerta. Su desktop
          la barra e' nascosta e resta la sola striscia. Dentro l'app niente
          striscia (upsell). Il contenitore pubblica la sua altezza e la lista
          lascia spazio sotto perche' l'ultimo file resti leggibile. */}
      <div className="vz-fondo-fisso">
        {!vistaSpeciale && (
          <div className="vz-mobile-uploadbar">
            <button className="vz-mobile-uploadbtn vz-mobile-uploadbtn-primary" onClick={()=>fileInputRef.current.click()}>
              <IconUpload width={15} height={15}/> {t('btn_upload_file_mobile')}
            </button>
            {canPickDirectory && (
              <button className="vz-mobile-uploadbtn vz-mobile-uploadbtn-ghost" onClick={()=>folderInputRef.current.click()}>
                📁 {t('btn_upload_folder_mobile')}
              </button>
            )}
          </div>
        )}
        {/* showUpgradeStrip, NON showUpgradePill (2026-09-16 sera): il
            guardiano !welcomeWindowOpen spegneva la striscia per le 48 ore
            dopo la registrazione — proprio quando il nuovo iscritto decide.
            La striscia e' l'unica offerta nel vault con i file, e mostra lo
            stesso prezzo dell'offerta di benvenuto (79). */}
        {!dentroApp() && showUpgradeStrip && !vaultDelTuttoVuoto && (
          <BannerOfferta posto="fondo" soloSeGratuito pianoUtente={planId} />
        )}
      </div>

      <SupportButton ref={supportRef} sidebarOpen={sidebarOpen} />
      <ShareModal target={shareTarget} allFolders={allFolders} onClose={()=>setShareTarget(null)} />
      <SharedFilesModal open={sharedModalOpen} onClose={()=>setSharedModalOpen(false)} />
      <VersionHistoryModal file={versionsTarget} onClose={()=>setVersionsTarget(null)} onRestored={()=>loadFiles(currentFolder)} />

      <DockMenu
        open={profileMenuOpen}
        ancoraRef={menuDaAccount ? bottoneAccountRef : null}
        onClose={()=>setOpenFlyout(o => o === 'profile' ? null : o)}
        user={user} planLabel={planLabel} planEmoji={planEmoji} planColor={planColor}
        items={[
          { icon:<IconProfile/>, label:t('dockmenu_profile'), action:onProfile },
          { icon:<IconFamily/>,  label:t('dockmenu_family'),  action:()=>toggleFlyout('family') },
          // Mobile only — desktop keeps the header's view-toggle buttons
          // (.vz-view-toggle), which the 700px query hides in favor of this.
          ...(window.innerWidth <= 700 ? [{
            type:'segmented', icon:'🖼', label:t('dockmenu_view'), value:viewMode, onChange:setViewMode,
            options:[
              { value:'list', icon:'≡', label:t('list_view_label') },
              { value:'grid', icon:'⊞', label:t('grid_view_label') },
            ],
          }] : []),
          { icon:<IconSupport/>, label:t('dockmenu_support'), action:()=>supportRef.current?.open() },
          ...(Capacitor.isNativePlatform()
            ? []
            : [{ icon:<IconPlans/>, label:t('dockmenu_plans'), action:onUpgrade }]),
          ...(isAdmin ? [{ icon:'⚙️', label:t('admin'), action:onAdmin }] : []),
          { icon:'📄', label:t('dockmenu_terms'),   action:onTerms },
          { icon:'🔒', label:t('dockmenu_privacy'), action:onPrivacy },
          { icon:<IconSignOut/>, label:t('sign_out'), action:onLogout, danger:true },
        ]}
      />
      <FamilyFlyout open={familyMenuOpen} onClose={closeFlyout} />
      <SearchFlyout open={searchOpen} onClose={closeFlyout} onSearch={searchAllFiles}
        onOpenFolder={handleSearchOpenFolder} onOpenFile={handleSearchOpenFile} />
      <Dock items={[
        { key:'search',  icon:<IconSearch/>,   label:t('dock_search'),  action:()=>toggleFlyout('search'), active: searchOpen },
        { key:'profile', icon:<IconProfile/>, label:t('dock_profile'), action:()=>{ setMenuDaAccount(false); toggleFlyout('profile'); }, active: profileMenuOpen },
        { key:'family',  icon:<IconFamily/>,  label:t('dock_family'),  action:()=>toggleFlyout('family'), active: familyMenuOpen },
        { key:'support', icon:<IconSupport/>, label:t('dock_support'), action:()=>supportRef.current?.open() },
        // Dentro l'app la voce Piani non c'e': porta a un percorso d'acquisto,
        // e dentro un'app quel percorso non e' ammesso. Sul sito resta.
        ...(Capacitor.isNativePlatform()
          ? []
          : [{ key:'plans', icon:<IconPlans/>, label:t('dock_plans'), action:onUpgrade, active: planId!=='lifetime_2tb' }]),
        ...(isAdmin ? [{ key:'admin', icon:'⚙️', label:t('admin'), action:onAdmin }] : []),
        { key:'logout',  icon:<IconSignOut/>, label:t('sign_out'), action:onLogout },
      ]} />
    </div>
    </>
  );
}
